import { LILODATA_BLOCK_ID } from '../constants.js'
import { createLilodataBlock, extractLilodataBlock, isLilodataBlock, parseLilodataText, updateLilodataText } from '../lilodata.js'
import type { Block, SpaceEntry } from '../types.js'
import { deriveSpaceIded } from './spaceIdentity.js'
import { EDITOR_BLOCKS_STORE, EditorMemory, cloneBlock, sortBlocks, waitForTransaction } from './editorMemory.js'
import { cloneSpaceEntry, decodeSpacePayload, encodeSpacePayload } from './spaceCodec.js'
import { hasCleanSeedMetadata } from './sync.js'
import { selectLandingSpace } from './indexNavigation.js'

export const LILO_DATABASE_NAME = 'lilo-write'
// v4 is a rollout barrier: opening the upgraded build evicts older tabs before
// update-only shelf writes can run against the same origin.
export const LILO_DATABASE_VERSION = 4
export const LEGACY_BLOCKS_STORE = 'blocks'
export const SPACES_META_STORE = 'spaces_meta'
export const SPACES_BLOBS_STORE = 'spaces_blobs'
export const LOCAL_STATE_STORE = 'local_state'
export const LOCAL_STATE_PASSPORT_KEY = 'passport'
export const LOCAL_STATE_WORKBENCH_KEY = 'workbench'

export type LocalAccountProfile = {
  username: string | null
  account_state: 'sync' | 'deactivated'
  stripe_cancel_at_period_end: boolean
  stripe_current_period_end?: string
}

export type LocalAccountPassport =
  | {
      kind: 'unverified'
      owner_id: string
      mount_id: string
    }
  | {
      kind: 'verified'
      owner_id: string
      mount_id: string
      profile: LocalAccountProfile
    }

export type MountedVolumeAuthority = {
  owner_id: string
  mount_id: string
}

export type LocalVolumeAuthority = MountedVolumeAuthority | null

export type LocalWorkbenchMarker =
  | {
      owner_id: null
      mount_id: null
      space_id: string
    }
  | {
      owner_id: string
      mount_id: string
      space_id: string
    }

type LocalPassportRow = {
  key: typeof LOCAL_STATE_PASSPORT_KEY
  value: LocalAccountPassport
}

type LocalWorkbenchRow = {
  key: typeof LOCAL_STATE_WORKBENCH_KEY
  value: LocalWorkbenchMarker
}

export type PayloadState = 'known' | 'hydrated' | 'missing'

export type LocalSpaceAvailability = 'ready' | 'unavailable' | 'damaged'

export type ShelfOwnerLane = {
  ownerId: string | null
}

export type LocalSpaceMeta = {
  owner_id: string | null
  space_id: string
  space_slug: string | null
  space_ed: string
  size_bytes: number
  published: boolean
  sync_at: string | null
  deleted_at: string | null
  dirty: boolean
  dirty_since: string | null
  payload_state: PayloadState
  payload_error_at: string | null
  last_opened_at: string | null
  last_local_save_at: string | null
  last_sync_ok_at: string | null
  in_flight_tab_id: string | null
  in_flight_started_at: string | null
}

export type LocalMemoryIndexRow = Pick<
  LocalSpaceMeta,
  | 'owner_id'
  | 'space_id'
  | 'space_slug'
  | 'space_ed'
  | 'size_bytes'
  | 'deleted_at'
  | 'last_opened_at'
> & {
  availability: LocalSpaceAvailability
}

export type SpaceBlobRow = {
  space_id: string
  payload: string
}

export type SpacePayloadProof = {
  space_id: string
  space_ed: string
  payload: string
}

export type SeedSpaceProof = SpacePayloadProof

export type WorkbenchRecoveryEvidence = {
  selection: 'marker' | 'landing'
  expected_workbench?: LocalWorkbenchMarker
  expected_editor_blocks: SpaceEntry[]
  target: SpacePayloadProof
  disposable_editor?: SpacePayloadProof
}

export type WriteLocalSpaceInput = {
  expectedPayload?: string
  blocksWithMeta: SpaceEntry[]
  ownerId?: string | null
  spaceSlug?: string | null
  dirty?: boolean
  payloadState?: PayloadState
  now?: string
}

export type VolumeSpaceWriteInput = Omit<WriteLocalSpaceInput, 'ownerId'> & {
  authority: LocalVolumeAuthority
  workbench: boolean
}

export type WriteVolumeSpaceResult =
  | { ok: true, meta: LocalSpaceMeta }
  | {
      ok: false
      reason:
        | 'authority_mismatch'
        | 'volume_mismatch'
        | 'local_changed'
        | 'space_exists'
        | 'space_missing'
        | 'space_deleted'
    }

export type WorkbenchWriteResult =
  | { ok: true }
  | {
      ok: false
      reason:
        | 'authority_mismatch'
        | 'volume_mismatch'
        | 'space_missing'
        | 'space_deleted'
        | 'integrity_mismatch'
    }

export type LocalVolumeBootSnapshot = {
  passport?: LocalAccountPassport
  workbench?: LocalWorkbenchMarker
  editor_blocks: SpaceEntry[]
  spaces_meta: LocalSpaceMeta[]
  active_blob?: SpaceBlobRow
}

export type LocalVolumeBootDamage =
  | 'invalid_passport'
  | 'invalid_workbench'
  | 'invalid_owner'
  | 'mixed_owners'
  | 'orphan_blob'
  | 'editor_identity'

export type LocalVolumeBootResolution =
  | {
      ok: true
      status: 'anonymous' | 'account' | 'account-recorded'
      snapshot: LocalVolumeBootSnapshot
    }
  | {
      ok: false
      reason: LocalVolumeBootDamage
    }

export type LocalSpaceFlagPatch = {
  dirty?: boolean
  payloadState?: PayloadState
  payloadErrorAt?: string | null
  spaceEd?: string
  lastLocalSaveAt?: string
}

export type RemoteSpaceMetaInput = {
  path?: string | null
  owner_id: string | null
  space_id: string
  space_slug?: string | null
  space_ed?: string | null
  size_bytes?: number | null
  published?: boolean | null
  sync_at?: string | null
  deleted_at?: string | null
}

export type LocalShelfSpace = {
  meta: LocalSpaceMeta
  blob?: SpaceBlobRow
}

export type VolumeShelfExportPreflight =
  | {
      ok: true
      spaces: Array<{
        meta: LocalSpaceMeta
        blob: SpaceBlobRow
      }>
    }
  | {
      ok: false
      reason: 'authority_mismatch' | 'volume_mismatch' | 'incomplete' | 'invalid_payload'
      space_id?: string
    }

export type UnsafeOwnerShelfReason =
  | 'dirty'
  | 'tombstone_unsynced'
  | 'sync_in_flight'
  | 'missing_sync_proof'

export type UnsafeOwnerShelfRow = {
  meta: LocalSpaceMeta
  reason: UnsafeOwnerShelfReason
}

export type AccountUnmountAuthority = MountedVolumeAuthority

export type AccountUnmountMode = 'guarded' | 'confirmed'

export type AccountVolumeMountMode = 'import-anonymous' | 'replace-anonymous'

export type AccountVolumeMountInput = {
  passport: Extract<LocalAccountPassport, { kind: 'verified' }>
  mode: AccountVolumeMountMode
  catalogue: RemoteSpaceMetaInput[]
  now?: string
}

export type AccountVolumeMountResult =
  | {
      ok: true
      status: 'mounted' | 'already-mounted'
      passport: LocalAccountPassport
    }
  | {
      ok: false
      reason: 'authority_mismatch' | 'workbench_mismatch' | 'volume_mismatch'
    }

export type AccountUnmountResult =
  | {
      ok: true
      status: 'unmounted' | 'already-unmounted'
    }
  | {
      ok: false
      reason: 'unsafe'
      unsafe_rows: UnsafeOwnerShelfRow[]
    }
  | {
      ok: false
      reason: 'authority_mismatch' | 'workbench_mismatch' | 'volume_mismatch'
    }

export type RemoteReplaceIntent = 'restore-from-cloud' | 'discard-local-changes'

export type ReplaceLocalBlobFromRemoteResult =
  | { ok: true, meta: LocalSpaceMeta }
  | { ok: false, reason: 'local_dirty' | 'local_deleted' | 'invalid_payload' | 'owner_mismatch' }

export type ReplaceLocalBlobFromRemoteInput = {
  remoteMeta: RemoteSpaceMetaInput
  payload: string
  now?: string
  intent?: RemoteReplaceIntent
}

export type SyncAcknowledgement = {
  spaceSlug?: string | null
  spaceEd?: string | null
  sent?: { spaceEd: string, payload?: string }
}

export type WriteMergedLocalSpaceInput = WriteLocalSpaceInput & {
  dirty?: boolean
}

export class DatabaseMemory {
  private db?: IDBDatabase
  // Only the open that actually crosses into v4 may reconstruct a legacy
  // Anonymous workbench marker. There is deliberately no persistent receipt.
  private mayRestoreLegacyWorkbench = false

  constructor(private readonly dbName = LILO_DATABASE_NAME) {}

  async open(): Promise<void> {
    if (this.db) return
    this.mayRestoreLegacyWorkbench = false

    await new Promise<void>((resolve, reject) => {
      const request = indexedDB.open(this.dbName, LILO_DATABASE_VERSION)
      let settled = false
      let blocked = false
      const fail = (error: unknown): void => {
        if (settled) return
        settled = true
        reject(error)
      }

      request.onerror = () => fail(request.error ?? new Error('Database open failed'))
      request.onblocked = () => {
        blocked = true
        fail(new Error('Database upgrade blocked'))
      }
      request.onupgradeneeded = (event) => {
        if (blocked) {
          request.transaction?.abort()
          return
        }
        const db = request.result
        this.mayRestoreLegacyWorkbench = event.oldVersion > 0
          && event.oldVersion < LILO_DATABASE_VERSION
        ensureObjectStores(db)
        if (!request.transaction) {
          throw new Error('Upgrade transaction unavailable')
        }
        if (event.oldVersion < 2) {
          migrateLegacyBlocksDuringUpgrade(db, request.transaction)
        }
      }
      request.onsuccess = () => {
        const db = request.result
        if (blocked || settled) {
          db.close()
          return
        }
        db.onversionchange = () => {
          db.close()
          if (this.db === db) {
            this.db = undefined
          }
        }
        this.db = db
        settled = true
        resolve()
      }
    })
  }

  close(): void {
    this.db?.close()
    this.db = undefined
    this.mayRestoreLegacyWorkbench = false
  }

  getEditorMemory(): EditorMemory {
    return new EditorMemory(this.requireDb())
  }

  async resolveVolumeBoot(mountId: string): Promise<LocalVolumeBootResolution> {
    const nextMountId = mountId.trim()
    if (!nextMountId || nextMountId !== mountId) {
      throw new Error('Volume boot mount id is invalid')
    }
    const tx = this.requireDb().transaction(
      [LOCAL_STATE_STORE, EDITOR_BLOCKS_STORE, SPACES_META_STORE, SPACES_BLOBS_STORE],
      'readwrite'
    )
    const localStateStore = tx.objectStore(LOCAL_STATE_STORE)
    const editorStore = tx.objectStore(EDITOR_BLOCKS_STORE)
    const metaStore = tx.objectStore(SPACES_META_STORE)
    const blobStore = tx.objectStore(SPACES_BLOBS_STORE)
    let passportRow: unknown
    let workbenchRow: unknown
    let editorBlocks: SpaceEntry[] = []
    let metaRows: LocalSpaceMeta[] = []
    let blobKeys: IDBValidKey[] = []
    let pendingReads = 5
    let result: LocalVolumeBootResolution = { ok: false, reason: 'invalid_owner' }

    const finishSnapshot = (
      status: Extract<LocalVolumeBootResolution, { ok: true }>['status'],
      passport: LocalAccountPassport | undefined,
      workbench: LocalWorkbenchMarker | undefined
    ): void => {
      const settle = (activeBlob?: SpaceBlobRow): void => {
        result = {
          ok: true,
          status,
          snapshot: {
            passport: passport ? cloneLocalAccountPassport(passport) : undefined,
            workbench: workbench ? { ...workbench } : undefined,
            editor_blocks: editorBlocks.map(block => cloneSpaceEntry(block)),
            spaces_meta: metaRows.map(row => cloneLocalSpaceMeta(row)),
            active_blob: activeBlob ? { ...activeBlob } : undefined
          }
        }
      }
      if (!workbench) {
        settle()
        return
      }
      const activeBlobRequest = blobStore.get(workbench.space_id)
      activeBlobRequest.onsuccess = () => {
        settle(activeBlobRequest.result as SpaceBlobRow | undefined)
      }
      activeBlobRequest.onerror = () => tx.abort()
    }

    const evaluate = (): void => {
      const passport = passportRow === undefined
        ? undefined
        : readValidPassportRow(passportRow)
      if (passportRow !== undefined && !passport) {
        result = { ok: false, reason: 'invalid_passport' }
        return
      }
      let workbench = workbenchRow === undefined
        ? undefined
        : readValidWorkbenchRow(workbenchRow)
      if (workbenchRow !== undefined && !workbench) {
        result = { ok: false, reason: 'invalid_workbench' }
        return
      }
      if (metaRows.some(row => !hasValidLocalSpaceIdentity(row))) {
        result = { ok: false, reason: 'invalid_owner' }
        return
      }
      const metaBySpaceId = new Map(metaRows.map(row => [row.space_id, row]))
      if (blobKeys.some(key => typeof key !== 'string' || !metaBySpaceId.has(key))) {
        result = { ok: false, reason: 'orphan_blob' }
        return
      }

      const owners = new Set(metaRows.map(row => row.owner_id))
      let ownerId: string | null
      if (passport) {
        ownerId = passport.owner_id
        if (owners.size > 1 || (owners.size === 1 && !owners.has(ownerId))) {
          result = { ok: false, reason: 'mixed_owners' }
          return
        }
      } else {
        if (owners.size > 1) {
          result = { ok: false, reason: 'mixed_owners' }
          return
        }
        ownerId = owners.values().next().value ?? null
      }

      if (workbench) {
        const authorityMatches = passport
          ? (
              workbench.owner_id === passport.owner_id
              && workbench.mount_id === passport.mount_id
            )
          : ownerId === null
            ? workbench.owner_id === null && workbench.mount_id === null
            : false
        if (!authorityMatches) {
          result = { ok: false, reason: 'invalid_workbench' }
          return
        }
      }

      if (passport) {
        finishSnapshot('account', passport, workbench)
        return
      }
      if (ownerId === null && !this.mayRestoreLegacyWorkbench) {
        finishSnapshot('anonymous', undefined, workbench)
        return
      }

      const recordResolvedVolume = (editorSpaceId?: string): void => {
        if (ownerId === null) {
          const resolvedWorkbench: LocalWorkbenchMarker | undefined = editorSpaceId
            ? {
                owner_id: null,
                mount_id: null,
                space_id: editorSpaceId
              }
            : undefined
          if (resolvedWorkbench) {
            localStateStore.put({
              key: LOCAL_STATE_WORKBENCH_KEY,
              value: resolvedWorkbench
            } satisfies LocalWorkbenchRow)
          }
          finishSnapshot('anonymous', undefined, resolvedWorkbench)
          return
        }
        const resolvedPassport: LocalAccountPassport = {
          kind: 'unverified',
          owner_id: ownerId,
          mount_id: nextMountId
        }
        localStateStore.put({
          key: LOCAL_STATE_PASSPORT_KEY,
          value: resolvedPassport
        } satisfies LocalPassportRow)
        const resolvedWorkbench: LocalWorkbenchMarker | undefined = editorSpaceId
          ? {
              owner_id: resolvedPassport.owner_id,
              mount_id: resolvedPassport.mount_id,
              space_id: editorSpaceId
            }
          : undefined
        if (resolvedWorkbench) {
          localStateStore.put({
            key: LOCAL_STATE_WORKBENCH_KEY,
            value: resolvedWorkbench
          } satisfies LocalWorkbenchRow)
        }
        finishSnapshot('account-recorded', resolvedPassport, resolvedWorkbench)
      }

      if (!editorBlocks.length) {
        recordResolvedVolume()
        return
      }
      let editorSpaceId: string | undefined
      try {
        editorSpaceId = extractLilodataBlock(editorBlocks).metaBlock?.ided[0]
      } catch {
        result = { ok: false, reason: 'editor_identity' }
        return
      }
      const activeRow = editorSpaceId ? metaBySpaceId.get(editorSpaceId) : undefined
      if (!activeRow || activeRow.deleted_at || activeRow.owner_id !== ownerId) {
        result = { ok: false, reason: 'editor_identity' }
        return
      }
      recordResolvedVolume(editorSpaceId)
    }

    const finishRead = (): void => {
      pendingReads -= 1
      if (pendingReads === 0) evaluate()
    }
    const abort = (): void => tx.abort()

    const passportRequest = localStateStore.get(LOCAL_STATE_PASSPORT_KEY)
    passportRequest.onsuccess = () => {
      passportRow = passportRequest.result
      finishRead()
    }
    passportRequest.onerror = abort

    const workbenchRequest = localStateStore.get(LOCAL_STATE_WORKBENCH_KEY)
    workbenchRequest.onsuccess = () => {
      workbenchRow = workbenchRequest.result
      finishRead()
    }
    workbenchRequest.onerror = abort

    const editorRequest = editorStore.getAll()
    editorRequest.onsuccess = () => {
      editorBlocks = [...(editorRequest.result ?? [])] as SpaceEntry[]
      finishRead()
    }
    editorRequest.onerror = abort

    const metaRequest = metaStore.getAll()
    metaRequest.onsuccess = () => {
      metaRows = [...(metaRequest.result ?? [])] as LocalSpaceMeta[]
      finishRead()
    }
    metaRequest.onerror = abort

    const blobKeysRequest = blobStore.getAllKeys()
    blobKeysRequest.onsuccess = () => {
      blobKeys = [...(blobKeysRequest.result ?? [])]
      finishRead()
    }
    blobKeysRequest.onerror = abort

    await waitForTransaction(tx)
    return result
  }

  async resolveAccountUnmountAuthority(): Promise<AccountUnmountAuthority | undefined> {
    const passport = await this.loadPassport()
    return passport
      ? {
          owner_id: passport.owner_id,
          mount_id: passport.mount_id
        }
      : undefined
  }

  async mountAccountVolume(input: AccountVolumeMountInput): Promise<AccountVolumeMountResult> {
    const ownerId = input.passport.owner_id.trim()
    const mountId = input.passport.mount_id.trim()
    if (!ownerId || !mountId) {
      return { ok: false, reason: 'authority_mismatch' }
    }
    const catalogue = input.catalogue
    if (catalogue.some(remote => remote.owner_id !== ownerId)) {
      return { ok: false, reason: 'authority_mismatch' }
    }
    const now = input.now ?? new Date().toISOString()
    const tx = this.requireDb().transaction(
      [LOCAL_STATE_STORE, EDITOR_BLOCKS_STORE, SPACES_META_STORE, SPACES_BLOBS_STORE],
      'readwrite'
    )
    const localStateStore = tx.objectStore(LOCAL_STATE_STORE)
    const editorStore = tx.objectStore(EDITOR_BLOCKS_STORE)
    const metaStore = tx.objectStore(SPACES_META_STORE)
    const blobStore = tx.objectStore(SPACES_BLOBS_STORE)
    let passport: LocalAccountPassport | undefined
    let marker: LocalWorkbenchMarker | undefined
    let editorBlocks: SpaceEntry[] = []
    let metaRows: LocalSpaceMeta[] = []
    let blobRows: SpaceBlobRow[] = []
    let pendingReads = 5
    let result: AccountVolumeMountResult = { ok: false, reason: 'authority_mismatch' }

    const writePassport = (
      currentMountId: string,
      status: 'mounted' | 'already-mounted',
      nextMarker?: LocalWorkbenchMarker
    ): void => {
      const nextPassport: LocalAccountPassport = {
        ...cloneLocalAccountPassport(input.passport),
        owner_id: ownerId,
        mount_id: currentMountId
      }
      const passportRow: LocalPassportRow = {
        key: LOCAL_STATE_PASSPORT_KEY,
        value: nextPassport
      }
      localStateStore.clear()
      if (nextMarker) {
        localStateStore.put({
          key: LOCAL_STATE_WORKBENCH_KEY,
          value: { ...nextMarker }
        } satisfies LocalWorkbenchRow)
      }
      localStateStore.put(passportRow)
      result = {
        ok: true,
        status,
        passport: cloneLocalAccountPassport(nextPassport)
      }
    }

    const evaluate = (): void => {
      const metaBySpaceId = new Map(metaRows.map(row => [row.space_id, row]))
      const liveRows = metaRows.filter(row => !row.deleted_at)
      const { metaBlock: editorMeta } = extractLilodataBlock(editorBlocks)
      const hasOrphanBlob = blobRows.some(blob => !metaBySpaceId.has(blob.space_id))
      if (hasOrphanBlob) {
        result = { ok: false, reason: 'volume_mismatch' }
        return
      }

      if (passport) {
        if (passport.owner_id !== ownerId) return
        if (metaRows.some(row => row.owner_id !== ownerId)) {
          result = { ok: false, reason: 'volume_mismatch' }
          return
        }
        const hasWorkbench = Boolean(marker || editorBlocks.length || liveRows.length)
        if (
          hasWorkbench
          && (
            !marker
            || marker.owner_id !== ownerId
            || marker.mount_id !== passport.mount_id
            || !liveRows.some(row => row.space_id === marker?.space_id)
            || editorMeta?.ided[0] !== marker.space_id
          )
        ) {
          result = { ok: false, reason: 'workbench_mismatch' }
          return
        }
        writePassport(passport.mount_id, 'already-mounted', marker)
        return
      }

      if (metaRows.some(row => row.owner_id !== null)) {
        result = { ok: false, reason: 'volume_mismatch' }
        return
      }
      const hasAnonymousWorkbench = Boolean(marker || editorBlocks.length || liveRows.length)
      if (
        hasAnonymousWorkbench
        && (
          !marker
          || marker.owner_id !== null
          || marker.mount_id !== null
          || !liveRows.some(row => row.space_id === marker?.space_id)
          || editorMeta?.ided[0] !== marker.space_id
        )
      ) {
        result = { ok: false, reason: 'workbench_mismatch' }
        return
      }

      const shouldImport = input.mode === 'import-anonymous' && liveRows.length > 0
      if (shouldImport) {
        const importedById = new Map<string, LocalSpaceMeta>()
        metaRows.forEach(row => {
          const imported = {
            ...row,
            owner_id: ownerId,
            dirty: true,
            dirty_since: row.dirty_since ?? now,
            sync_at: null,
            last_sync_ok_at: null,
            in_flight_tab_id: null,
            in_flight_started_at: null,
            last_local_save_at: row.last_local_save_at ?? now
          }
          importedById.set(imported.space_id, imported)
          metaStore.put(imported)
        })
        catalogue.forEach(remote => {
          metaStore.put(buildRemoteKnownMeta(
            importedById.get(remote.space_id),
            remote,
            now
          ))
        })
        const nextMarker: LocalWorkbenchMarker = {
          owner_id: ownerId,
          mount_id: mountId,
          space_id: marker!.space_id
        }
        writePassport(mountId, 'mounted', nextMarker)
        return
      }

      editorStore.clear()
      metaStore.clear()
      blobStore.clear()
      catalogue.forEach(remote => {
        metaStore.put(buildRemoteKnownMeta(undefined, remote, now))
      })
      writePassport(mountId, 'mounted')
    }

    const finishRead = (): void => {
      pendingReads -= 1
      if (pendingReads === 0) evaluate()
    }
    const abort = (): void => tx.abort()

    const passportRequest = localStateStore.get(LOCAL_STATE_PASSPORT_KEY)
    passportRequest.onsuccess = () => {
      passport = (passportRequest.result as LocalPassportRow | undefined)?.value
      finishRead()
    }
    passportRequest.onerror = abort

    const markerRequest = localStateStore.get(LOCAL_STATE_WORKBENCH_KEY)
    markerRequest.onsuccess = () => {
      marker = (markerRequest.result as LocalWorkbenchRow | undefined)?.value
      finishRead()
    }
    markerRequest.onerror = abort

    const editorRequest = editorStore.getAll()
    editorRequest.onsuccess = () => {
      editorBlocks = ((editorRequest.result ?? []) as SpaceEntry[]).map(block => cloneSpaceEntry(block))
      finishRead()
    }
    editorRequest.onerror = abort

    const metaRequest = metaStore.getAll()
    metaRequest.onsuccess = () => {
      metaRows = [...(metaRequest.result ?? [])] as LocalSpaceMeta[]
      finishRead()
    }
    metaRequest.onerror = abort

    const blobRequest = blobStore.getAll()
    blobRequest.onsuccess = () => {
      blobRows = [...(blobRequest.result ?? [])] as SpaceBlobRow[]
      finishRead()
    }
    blobRequest.onerror = abort

    await waitForTransaction(tx)
    return result
  }

  async unmountAccountVolume(
    authority: AccountUnmountAuthority,
    mode: AccountUnmountMode = 'guarded',
    safeSeedProofs: SeedSpaceProof[] = []
  ): Promise<AccountUnmountResult> {
    const tx = this.requireDb().transaction(
      [LOCAL_STATE_STORE, EDITOR_BLOCKS_STORE, SPACES_META_STORE, SPACES_BLOBS_STORE],
      'readwrite'
    )
    const localStateStore = tx.objectStore(LOCAL_STATE_STORE)
    const editorStore = tx.objectStore(EDITOR_BLOCKS_STORE)
    const metaStore = tx.objectStore(SPACES_META_STORE)
    const blobStore = tx.objectStore(SPACES_BLOBS_STORE)
    let passport: LocalAccountPassport | undefined
    let marker: LocalWorkbenchMarker | undefined
    let editorBlocks: SpaceEntry[] = []
    let metaRows: LocalSpaceMeta[] = []
    let blobRows: SpaceBlobRow[] = []
    let pendingReads = 5
    let result: AccountUnmountResult = { ok: false, reason: 'authority_mismatch' }

    const evaluate = (): void => {
      const ownedRows = metaRows.filter(row => row.owner_id === authority.owner_id)
      const blobBySpaceId = new Map(blobRows.map(row => [row.space_id, row]))
      const unsafeRows = ownedRows.flatMap(row => {
        const reason = resolveUnsafeOwnerShelfReason(row)
        const seedProof = safeSeedProofs.find(proof => proof.space_id === row.space_id)
        const isProvenSeed = Boolean(
          seedProof
          && matchesCleanSeedProof(row, blobBySpaceId.get(row.space_id), seedProof)
          && (
            marker?.space_id !== row.space_id
            || matchesBlocksPayloadProof(editorBlocks, seedProof)
          )
        )
        if (isProvenSeed) return []
        return reason ? [{ meta: cloneLocalSpaceMeta(row), reason }] : []
      })
      const ownedSpaceIds = new Set(ownedRows.map(row => row.space_id))
      const metaBySpaceId = new Map(metaRows.map(row => [row.space_id, row]))
      const { metaBlock: editorMeta } = extractLilodataBlock(editorBlocks)

      if (!passport) {
        const editorResolvedOutsideExpectedVolume =
          editorBlocks.length === 0
          || Boolean(
            editorMeta
            && metaBySpaceId.has(editorMeta.ided[0])
            && metaBySpaceId.get(editorMeta.ided[0])?.owner_id !== authority.owner_id
          )
        const hasExpectedOwnerMarker = marker?.owner_id === authority.owner_id
        const hasOrphanBlob = blobRows.some(blob => !metaBySpaceId.has(blob.space_id))
        if (
          ownedRows.length === 0
          && !hasExpectedOwnerMarker
          && editorResolvedOutsideExpectedVolume
          && !hasOrphanBlob
        ) {
          result = { ok: true, status: 'already-unmounted' }
        }
        return
      }
      if (!matchesMountedAuthority(passport, authority)) return
      if (
        !marker
        || marker.owner_id !== authority.owner_id
        || marker.mount_id !== authority.mount_id
        || !ownedSpaceIds.has(marker.space_id)
        || editorMeta?.ided[0] !== marker.space_id
      ) {
        result = { ok: false, reason: 'workbench_mismatch' }
        return
      }
      const hasForeignMeta = metaRows.some(row => row.owner_id !== authority.owner_id)
      const hasOrphanBlob = blobRows.some(blob => !ownedSpaceIds.has(blob.space_id))
      if (hasForeignMeta || hasOrphanBlob) {
        result = { ok: false, reason: 'volume_mismatch' }
        return
      }
      if (mode === 'guarded' && unsafeRows.length) {
        result = { ok: false, reason: 'unsafe', unsafe_rows: unsafeRows }
        return
      }

      editorStore.clear()
      metaStore.clear()
      blobStore.clear()
      localStateStore.clear()
      result = { ok: true, status: 'unmounted' }
    }

    const finishRead = (): void => {
      pendingReads -= 1
      if (pendingReads === 0) evaluate()
    }
    const abort = (): void => tx.abort()

    const passportRequest = localStateStore.get(LOCAL_STATE_PASSPORT_KEY)
    passportRequest.onsuccess = () => {
      passport = (passportRequest.result as LocalPassportRow | undefined)?.value
      finishRead()
    }
    passportRequest.onerror = abort

    const markerRequest = localStateStore.get(LOCAL_STATE_WORKBENCH_KEY)
    markerRequest.onsuccess = () => {
      marker = (markerRequest.result as LocalWorkbenchRow | undefined)?.value
      finishRead()
    }
    markerRequest.onerror = abort

    const editorRequest = editorStore.getAll()
    editorRequest.onsuccess = () => {
      editorBlocks = ((editorRequest.result ?? []) as SpaceEntry[]).map(block => cloneSpaceEntry(block))
      finishRead()
    }
    editorRequest.onerror = abort

    const metaRequest = metaStore.getAll()
    metaRequest.onsuccess = () => {
      metaRows = [...(metaRequest.result ?? [])] as LocalSpaceMeta[]
      finishRead()
    }
    metaRequest.onerror = abort

    const blobRequest = blobStore.getAll()
    blobRequest.onsuccess = () => {
      blobRows = [...(blobRequest.result ?? [])] as SpaceBlobRow[]
      finishRead()
    }
    blobRequest.onerror = abort

    await waitForTransaction(tx)
    return result
  }

  async loadPassport(): Promise<LocalAccountPassport | undefined> {
    const row = await readOne<LocalPassportRow>(
      this.requireDb(),
      LOCAL_STATE_STORE,
      LOCAL_STATE_PASSPORT_KEY
    )
    return row ? cloneLocalAccountPassport(row.value) : undefined
  }

  async loadWorkbenchMarker(): Promise<LocalWorkbenchMarker | undefined> {
    const row = await readOne<LocalWorkbenchRow>(
      this.requireDb(),
      LOCAL_STATE_STORE,
      LOCAL_STATE_WORKBENCH_KEY
    )
    return row ? { ...row.value } : undefined
  }

  async loadVolumeBootSnapshot(): Promise<LocalVolumeBootSnapshot> {
    const tx = this.requireDb().transaction(
      [LOCAL_STATE_STORE, EDITOR_BLOCKS_STORE, SPACES_META_STORE, SPACES_BLOBS_STORE],
      'readonly'
    )
    const localStateStore = tx.objectStore(LOCAL_STATE_STORE)
    let passport: LocalAccountPassport | undefined
    let workbench: LocalWorkbenchMarker | undefined
    let editorBlocks: SpaceEntry[] = []
    let metaRows: LocalSpaceMeta[] = []
    let activeBlob: SpaceBlobRow | undefined
    let pendingReads = 4

    const finishRead = (): void => {
      pendingReads -= 1
    }
    const abort = (): void => tx.abort()

    const passportRequest = localStateStore.get(LOCAL_STATE_PASSPORT_KEY)
    passportRequest.onsuccess = () => {
      passport = (passportRequest.result as LocalPassportRow | undefined)?.value
      finishRead()
    }
    passportRequest.onerror = abort

    const workbenchRequest = localStateStore.get(LOCAL_STATE_WORKBENCH_KEY)
    workbenchRequest.onsuccess = () => {
      workbench = (workbenchRequest.result as LocalWorkbenchRow | undefined)?.value
      if (workbench) {
        pendingReads += 1
        const blobRequest = tx.objectStore(SPACES_BLOBS_STORE).get(workbench.space_id)
        blobRequest.onsuccess = () => {
          activeBlob = blobRequest.result as SpaceBlobRow | undefined
          finishRead()
        }
        blobRequest.onerror = abort
      }
      finishRead()
    }
    workbenchRequest.onerror = abort

    const editorRequest = tx.objectStore(EDITOR_BLOCKS_STORE).getAll()
    editorRequest.onsuccess = () => {
      editorBlocks = [...(editorRequest.result ?? [])] as SpaceEntry[]
      finishRead()
    }
    editorRequest.onerror = abort

    const metaRequest = tx.objectStore(SPACES_META_STORE).getAll()
    metaRequest.onsuccess = () => {
      metaRows = [...(metaRequest.result ?? [])] as LocalSpaceMeta[]
      finishRead()
    }
    metaRequest.onerror = abort

    await waitForTransaction(tx)
    if (pendingReads !== 0) {
      throw new Error('Volume boot snapshot did not settle')
    }
    return {
      passport: passport ? cloneLocalAccountPassport(passport) : undefined,
      workbench: workbench ? { ...workbench } : undefined,
      editor_blocks: editorBlocks.map(block => cloneSpaceEntry(block)),
      spaces_meta: metaRows.map(row => cloneLocalSpaceMeta(row)),
      active_blob: activeBlob ? { ...activeBlob } : undefined
    }
  }

  async loadUnscopedRescueBlobs(editorFallbackSpaceId: string): Promise<SpaceBlobRow[]> {
    if (!isExactNonEmptyString(editorFallbackSpaceId)) {
      throw new Error('Rescue editor space id is invalid')
    }
    const tx = this.requireDb().transaction(
      [EDITOR_BLOCKS_STORE, SPACES_BLOBS_STORE],
      'readonly'
    )
    const editorRequest = tx.objectStore(EDITOR_BLOCKS_STORE).getAll()
    const blobsRequest = tx.objectStore(SPACES_BLOBS_STORE).getAll()
    let editorBlocks: SpaceEntry[] = []
    let blobs: SpaceBlobRow[] = []

    editorRequest.onsuccess = () => {
      editorBlocks = [...(editorRequest.result ?? [])] as SpaceEntry[]
    }
    editorRequest.onerror = () => tx.abort()
    blobsRequest.onsuccess = () => {
      blobs = [...(blobsRequest.result ?? [])] as SpaceBlobRow[]
    }
    blobsRequest.onerror = () => tx.abort()

    await waitForTransaction(tx)
    const bySpaceId = new Map(blobs.map(blob => [blob.space_id, { ...blob }]))
    if (editorBlocks.length) {
      const cloned = editorBlocks.map(block => cloneSpaceEntry(block))
      const { blocks, metaBlock } = extractLilodataBlock(cloned)
      const hasEditorIdentity = metaBlock && isExactNonEmptyString(metaBlock.ided[0])
      const spaceId = hasEditorIdentity ? metaBlock.ided[0] : editorFallbackSpaceId
      const rescueBlocks = hasEditorIdentity
        ? cloned
        : [
            ...blocks,
            createLilodataBlock('', {
              createdId: spaceId,
              editedId: deriveSpaceIded(blocks).editedId
            })
          ]
      bySpaceId.set(spaceId, {
        space_id: spaceId,
        payload: JSON.stringify(rescueBlocks)
      })
    }
    return [...bySpaceId.values()]
      .sort((a, b) => a.space_id.localeCompare(b.space_id))
      .map(blob => ({ ...blob }))
  }

  async compareAndSetPassport(
    expected: MountedVolumeAuthority | null,
    next: LocalAccountPassport | null
  ): Promise<boolean> {
    const tx = this.requireDb().transaction([LOCAL_STATE_STORE], 'readwrite')
    const store = tx.objectStore(LOCAL_STATE_STORE)
    const request = store.get(LOCAL_STATE_PASSPORT_KEY)
    let swapped = false

    request.onsuccess = () => {
      const row = request.result as LocalPassportRow | undefined
      if (!matchesPassportAuthority(row?.value, expected)) return
      if (expected && next && !matchesMountedAuthority(next, expected)) return

      swapped = true
      if (next) {
        const nextRow: LocalPassportRow = {
          key: LOCAL_STATE_PASSPORT_KEY,
          value: cloneLocalAccountPassport(next)
        }
        store.put(nextRow)
        return
      }
      store.delete(LOCAL_STATE_PASSPORT_KEY)
    }
    request.onerror = () => tx.abort()
    await waitForTransaction(tx)
    return swapped
  }

  async replaceEditorBlocksWithWorkbenchMarker(
    blocks: SpaceEntry[],
    marker: LocalWorkbenchMarker,
    consumeSeed?: SeedSpaceProof,
    recovery?: WorkbenchRecoveryEvidence
  ): Promise<WorkbenchWriteResult> {
    const tx = this.requireDb().transaction(
      [LOCAL_STATE_STORE, EDITOR_BLOCKS_STORE, SPACES_META_STORE, SPACES_BLOBS_STORE],
      'readwrite'
    )
    const localStateStore = tx.objectStore(LOCAL_STATE_STORE)
    const editorStore = tx.objectStore(EDITOR_BLOCKS_STORE)
    const metaStore = tx.objectStore(SPACES_META_STORE)
    const blobStore = tx.objectStore(SPACES_BLOBS_STORE)
    let result: WorkbenchWriteResult = {
      ok: false,
      reason: 'authority_mismatch'
    }
    const replace = (): void => {
      result = { ok: true }
      editorStore.clear()
      blocks.forEach(block => editorStore.put(cloneSpaceEntry(block), block.ided[0]))
      const markerRow: LocalWorkbenchRow = {
        key: LOCAL_STATE_WORKBENCH_KEY,
        value: { ...marker }
      }
      localStateStore.put(markerRow)
      if (consumeSeed) {
        metaStore.delete(consumeSeed.space_id)
        blobStore.delete(consumeSeed.space_id)
      }
    }

    if (!consumeSeed && recovery) {
      let passport: LocalAccountPassport | undefined
      let workbench: LocalWorkbenchMarker | undefined
      let editorBlocks: SpaceEntry[] = []
      let metaRows: LocalSpaceMeta[] = []
      let targetBlob: SpaceBlobRow | undefined
      let disposableBlob: SpaceBlobRow | undefined
      let pendingReads = recovery.disposable_editor ? 6 : 5
      const { metaBlock: targetEditorMeta } = extractLilodataBlock(blocks)
      const evaluate = (): void => {
        if (!matchesWorkbenchAuthority(passport, marker)) return
        const ownerId = marker.owner_id
        const targetMeta = metaRows.find(row => row.space_id === marker.space_id)
        const targetValid = Boolean(
          targetMeta
          && !targetMeta.deleted_at
          && targetMeta.owner_id === ownerId
          && targetMeta.space_ed === recovery.target.space_ed
          && targetEditorMeta?.ided[0] === marker.space_id
          && targetEditorMeta.ided[1] === targetMeta.space_ed
          && recovery.target.space_id === marker.space_id
          && targetBlob?.space_id === marker.space_id
          && targetBlob.payload === recovery.target.payload
          && matchesBlocksPayloadProof(blocks, recovery.target)
        )
        const alreadyRecovered = targetValid
          && matchesOptionalWorkbenchMarker(workbench, marker)
          && matchesBlocksExactly(editorBlocks, blocks)
        if (metaRows.some(row => row.owner_id !== ownerId)) {
          result = { ok: false, reason: 'integrity_mismatch' }
          return
        }
        if (alreadyRecovered) {
          result = { ok: true }
          return
        }
        if (
          !matchesOptionalWorkbenchMarker(workbench, recovery.expected_workbench)
          || !matchesBlocksExactly(editorBlocks, recovery.expected_editor_blocks)
          || !targetValid
        ) {
          result = { ok: false, reason: 'integrity_mismatch' }
          return
        }

        const landing = selectLandingSpace(metaRows)
        const markerSpaceId = workbench?.space_id
        const markerMeta = markerSpaceId
          ? metaRows.find(row => row.space_id === markerSpaceId)
          : undefined
        const selectionValid = recovery.selection === 'marker'
          ? Boolean(
              workbench
              && matchesVolumeMarkerAuthority(
                workbench,
                marker.owner_id === null
                  ? null
                  : { owner_id: marker.owner_id, mount_id: marker.mount_id }
              )
              && !markerMeta?.deleted_at
              && markerMeta?.space_id === marker.space_id
            )
          : Boolean(
              (!workbench || !markerMeta || markerMeta.deleted_at)
              && landing?.space_id === marker.space_id
            )
        if (!selectionValid) {
          result = { ok: false, reason: 'integrity_mismatch' }
          return
        }

        if (editorBlocks.length) {
          const proof = recovery.disposable_editor
          const proofMeta = proof
            ? metaRows.find(row => row.space_id === proof.space_id)
            : undefined
          const { metaBlock: editorMeta } = extractLilodataBlock(editorBlocks)
          if (
            !proof
            || !proofMeta
            || proofMeta.deleted_at
            || proofMeta.owner_id !== ownerId
            || proofMeta.space_ed !== proof.space_ed
            || editorMeta?.ided[0] !== proof.space_id
            || editorMeta.ided[1] !== proof.space_ed
            || disposableBlob?.space_id !== proof.space_id
            || disposableBlob.payload !== proof.payload
            || !matchesBlocksPayloadProof(editorBlocks, proof)
          ) {
            result = { ok: false, reason: 'integrity_mismatch' }
            return
          }
        } else if (recovery.disposable_editor) {
          result = { ok: false, reason: 'integrity_mismatch' }
          return
        }
        replace()
      }
      const finishRead = (): void => {
        pendingReads -= 1
        if (pendingReads === 0) evaluate()
      }
      const abort = (): void => tx.abort()

      const passportRequest = localStateStore.get(LOCAL_STATE_PASSPORT_KEY)
      passportRequest.onsuccess = () => {
        passport = (passportRequest.result as LocalPassportRow | undefined)?.value
        finishRead()
      }
      passportRequest.onerror = abort

      const markerRequest = localStateStore.get(LOCAL_STATE_WORKBENCH_KEY)
      markerRequest.onsuccess = () => {
        workbench = (markerRequest.result as LocalWorkbenchRow | undefined)?.value
        finishRead()
      }
      markerRequest.onerror = abort

      const editorRequest = editorStore.getAll()
      editorRequest.onsuccess = () => {
        editorBlocks = ((editorRequest.result ?? []) as SpaceEntry[]).map(block => cloneSpaceEntry(block))
        finishRead()
      }
      editorRequest.onerror = abort

      const metaRequest = metaStore.getAll()
      metaRequest.onsuccess = () => {
        metaRows = [...(metaRequest.result ?? [])] as LocalSpaceMeta[]
        finishRead()
      }
      metaRequest.onerror = abort

      const targetBlobRequest = blobStore.get(marker.space_id)
      targetBlobRequest.onsuccess = () => {
        targetBlob = targetBlobRequest.result as SpaceBlobRow | undefined
        finishRead()
      }
      targetBlobRequest.onerror = abort

      if (recovery.disposable_editor) {
        const disposableBlobRequest = blobStore.get(recovery.disposable_editor.space_id)
        disposableBlobRequest.onsuccess = () => {
          disposableBlob = disposableBlobRequest.result as SpaceBlobRow | undefined
          finishRead()
        }
        disposableBlobRequest.onerror = abort
      }

      await waitForTransaction(tx)
      return result
    }

    if (!consumeSeed) {
      let passport: LocalAccountPassport | undefined
      let targetMeta: LocalSpaceMeta | undefined
      let pendingReads = 2
      const evaluate = (): void => {
        if (!matchesWorkbenchAuthority(passport, marker)) return
        if (!targetMeta) {
          result = { ok: false, reason: 'space_missing' }
          return
        }
        if (targetMeta.owner_id !== marker.owner_id) {
          result = { ok: false, reason: 'volume_mismatch' }
          return
        }
        if (targetMeta.deleted_at) {
          result = { ok: false, reason: 'space_deleted' }
          return
        }
        replace()
      }
      const finishRead = (): void => {
        pendingReads -= 1
        if (pendingReads === 0) evaluate()
      }
      const passportRequest = localStateStore.get(LOCAL_STATE_PASSPORT_KEY)
      passportRequest.onsuccess = () => {
        passport = (passportRequest.result as LocalPassportRow | undefined)?.value
        finishRead()
      }
      passportRequest.onerror = () => tx.abort()
      const targetMetaRequest = metaStore.get(marker.space_id)
      targetMetaRequest.onsuccess = () => {
        targetMeta = targetMetaRequest.result as LocalSpaceMeta | undefined
        finishRead()
      }
      targetMetaRequest.onerror = () => tx.abort()
      await waitForTransaction(tx)
      return result
    }

    let passport: LocalAccountPassport | undefined
    let workbench: LocalWorkbenchMarker | undefined
    let seedMeta: LocalSpaceMeta | undefined
    let seedBlob: SpaceBlobRow | undefined
    let targetMeta: LocalSpaceMeta | undefined
    let targetBlob: SpaceBlobRow | undefined
    let editorBlocks: SpaceEntry[] = []
    let pendingReads = 7
    const { metaBlock: targetEditorMeta } = extractLilodataBlock(blocks)
    const evaluate = (): void => {
      if (!matchesWorkbenchAuthority(passport, marker)) return
      if (
        !workbench
        || !matchesVolumeMarkerAuthority(workbench, marker.owner_id === null
          ? null
          : { owner_id: marker.owner_id, mount_id: marker.mount_id })
        || workbench.space_id !== consumeSeed.space_id
        || marker.space_id === consumeSeed.space_id
        || !targetMeta
        || !targetBlob
        || targetMeta.deleted_at
        || targetMeta.owner_id !== marker.owner_id
        || targetMeta.space_ed !== targetEditorMeta?.ided[1]
        || targetBlob.space_id !== marker.space_id
        || !matchesBlocksPayloadProof(blocks, {
          space_id: marker.space_id,
          space_ed: targetMeta.space_ed,
          payload: targetBlob.payload
        })
        || seedMeta?.owner_id !== marker.owner_id
        || !matchesCleanSeedProof(seedMeta, seedBlob, consumeSeed)
        || !matchesBlocksPayloadProof(editorBlocks, consumeSeed)
      ) {
        result = { ok: false, reason: 'integrity_mismatch' }
        return
      }
      replace()
    }
    const finishRead = (): void => {
      pendingReads -= 1
      if (pendingReads === 0) evaluate()
    }
    const abort = (): void => tx.abort()

    const passportRequest = localStateStore.get(LOCAL_STATE_PASSPORT_KEY)
    passportRequest.onsuccess = () => {
      passport = (passportRequest.result as LocalPassportRow | undefined)?.value
      finishRead()
    }
    passportRequest.onerror = abort

    const markerRequest = localStateStore.get(LOCAL_STATE_WORKBENCH_KEY)
    markerRequest.onsuccess = () => {
      workbench = (markerRequest.result as LocalWorkbenchRow | undefined)?.value
      finishRead()
    }
    markerRequest.onerror = abort

    const seedMetaRequest = metaStore.get(consumeSeed.space_id)
    seedMetaRequest.onsuccess = () => {
      seedMeta = seedMetaRequest.result as LocalSpaceMeta | undefined
      finishRead()
    }
    seedMetaRequest.onerror = abort

    const seedBlobRequest = blobStore.get(consumeSeed.space_id)
    seedBlobRequest.onsuccess = () => {
      seedBlob = seedBlobRequest.result as SpaceBlobRow | undefined
      finishRead()
    }
    seedBlobRequest.onerror = abort

    const targetMetaRequest = metaStore.get(marker.space_id)
    targetMetaRequest.onsuccess = () => {
      targetMeta = targetMetaRequest.result as LocalSpaceMeta | undefined
      finishRead()
    }
    targetMetaRequest.onerror = abort

    const targetBlobRequest = blobStore.get(marker.space_id)
    targetBlobRequest.onsuccess = () => {
      targetBlob = targetBlobRequest.result as SpaceBlobRow | undefined
      finishRead()
    }
    targetBlobRequest.onerror = abort

    const editorRequest = editorStore.getAll()
    editorRequest.onsuccess = () => {
      editorBlocks = ((editorRequest.result ?? []) as SpaceEntry[]).map(block => cloneSpaceEntry(block))
      finishRead()
    }
    editorRequest.onerror = abort

    await waitForTransaction(tx)
    return result
  }

  async fileVolumeSpace(input: VolumeSpaceWriteInput): Promise<WriteVolumeSpaceResult> {
    return this.writeVolumeSpace(input, 'file')
  }

  async updateVolumeSpace(input: VolumeSpaceWriteInput): Promise<WriteVolumeSpaceResult> {
    return this.writeVolumeSpace(input, 'update')
  }

  private async writeVolumeSpace(
    input: VolumeSpaceWriteInput,
    intent: 'file' | 'update'
  ): Promise<WriteVolumeSpaceResult> {
    const now = input.now ?? new Date().toISOString()
    const payload = await encodeSpacePayload(input.blocksWithMeta)
    const ownerId = input.authority?.owner_id ?? null
    const nextMeta = buildLocalSpaceMeta(input.blocksWithMeta, payload, {
      ownerId,
      spaceSlug: input.spaceSlug,
      dirty: input.dirty,
      payloadState: input.payloadState,
      now
    })
    const tx = this.requireDb().transaction(
      [LOCAL_STATE_STORE, EDITOR_BLOCKS_STORE, SPACES_META_STORE, SPACES_BLOBS_STORE],
      'readwrite'
    )
    const localStateStore = tx.objectStore(LOCAL_STATE_STORE)
    const editorStore = tx.objectStore(EDITOR_BLOCKS_STORE)
    const metaStore = tx.objectStore(SPACES_META_STORE)
    const blobStore = tx.objectStore(SPACES_BLOBS_STORE)
    let passport: LocalAccountPassport | undefined
    let existing: LocalSpaceMeta | undefined
    let pendingReads = 3
    let existingPayload: string | undefined
    let result: WriteVolumeSpaceResult = { ok: false, reason: 'authority_mismatch' }

    const evaluate = (): void => {
      if (!matchesPassportAuthority(passport, input.authority)) return
      if (existing && existing.owner_id !== ownerId) {
        result = { ok: false, reason: 'volume_mismatch' }
        return
      }
      if (intent === 'file' && existing) {
        result = {
          ok: false,
          reason: existing.deleted_at ? 'space_deleted' : 'space_exists'
        }
        return
      }
      if (intent === 'update' && !existing) {
        result = { ok: false, reason: 'space_missing' }
        return
      }
      if (intent === 'update' && existing?.deleted_at) {
        result = { ok: false, reason: 'space_deleted' }
        return
      }
      if (input.expectedPayload !== undefined && existingPayload !== input.expectedPayload) {
        result = { ok: false, reason: 'local_changed' }
        return
      }
      const authoredChange = Boolean(ownerId && input.dirty === undefined && existingPayload !== payload)
      if (authoredChange) {
        nextMeta.dirty = true
        nextMeta.dirty_since = existing?.dirty_since ?? now
      }
      const meta = intent === 'update'
        ? mergeLocalSpaceWriteMeta(existing, nextMeta, {
            ...input,
            dirty: authoredChange ? true : input.dirty,
            ownerId
          })
        : nextMeta
      blobStore.put({ space_id: meta.space_id, payload })
      metaStore.put(meta)

      if (input.workbench) {
        editorStore.clear()
        input.blocksWithMeta.forEach(block => {
          editorStore.put(cloneSpaceEntry(block), block.ided[0])
        })
        const markerRow: LocalWorkbenchRow = {
          key: LOCAL_STATE_WORKBENCH_KEY,
          value: input.authority
            ? {
                owner_id: input.authority.owner_id,
                mount_id: input.authority.mount_id,
                space_id: meta.space_id
              }
            : {
                owner_id: null,
                mount_id: null,
                space_id: meta.space_id
              }
        }
        localStateStore.put(markerRow)
      }
      result = { ok: true, meta: cloneLocalSpaceMeta(meta) }
    }

    const finishRead = (): void => {
      pendingReads -= 1
      if (pendingReads === 0) evaluate()
    }
    const abort = (): void => tx.abort()

    {
      const blobRequest = blobStore.get(nextMeta.space_id)
      blobRequest.onsuccess = () => {
        existingPayload = (blobRequest.result as SpaceBlobRow | undefined)?.payload
        finishRead()
      }
      blobRequest.onerror = abort
    }

    const passportRequest = localStateStore.get(LOCAL_STATE_PASSPORT_KEY)
    passportRequest.onsuccess = () => {
      passport = (passportRequest.result as LocalPassportRow | undefined)?.value
      finishRead()
    }
    passportRequest.onerror = abort

    const existingRequest = metaStore.get(nextMeta.space_id)
    existingRequest.onsuccess = () => {
      existing = existingRequest.result as LocalSpaceMeta | undefined
      finishRead()
    }
    existingRequest.onerror = abort

    await waitForTransaction(tx)
    return result
  }

  async clearLocalMemoryForVolume(authority: LocalVolumeAuthority): Promise<boolean> {
    const tx = this.requireDb().transaction(
      [LOCAL_STATE_STORE, EDITOR_BLOCKS_STORE, SPACES_META_STORE, SPACES_BLOBS_STORE],
      'readwrite'
    )
    const localStateStore = tx.objectStore(LOCAL_STATE_STORE)
    const metaStore = tx.objectStore(SPACES_META_STORE)
    let passport: LocalAccountPassport | undefined
    let metaRows: LocalSpaceMeta[] = []
    let blobKeys: IDBValidKey[] = []
    let pendingReads = 3
    let cleared = false

    const evaluate = (): void => {
      if (!matchesPassportAuthority(passport, authority)) return
      const ownerId = authority?.owner_id ?? null
      if (metaRows.some(row => row.owner_id !== ownerId)) return
      const metaIds = new Set(metaRows.map(row => row.space_id))
      if (
        blobKeys.some(key => (
          typeof key !== 'string'
          || !metaIds.has(key)
        ))
      ) return
      cleared = true
      tx.objectStore(EDITOR_BLOCKS_STORE).clear()
      metaStore.clear()
      tx.objectStore(SPACES_BLOBS_STORE).clear()
      localStateStore.delete(LOCAL_STATE_WORKBENCH_KEY)
    }
    const finishRead = (): void => {
      pendingReads -= 1
      if (pendingReads === 0) evaluate()
    }
    const abort = (): void => tx.abort()

    const passportRequest = localStateStore.get(LOCAL_STATE_PASSPORT_KEY)
    passportRequest.onsuccess = () => {
      passport = (passportRequest.result as LocalPassportRow | undefined)?.value
      finishRead()
    }
    passportRequest.onerror = abort

    const metaRequest = metaStore.getAll()
    metaRequest.onsuccess = () => {
      metaRows = [...(metaRequest.result ?? [])] as LocalSpaceMeta[]
      finishRead()
    }
    metaRequest.onerror = abort

    const blobKeysRequest = tx.objectStore(SPACES_BLOBS_STORE).getAllKeys()
    blobKeysRequest.onsuccess = () => {
      blobKeys = [...(blobKeysRequest.result ?? [])]
      finishRead()
    }
    blobKeysRequest.onerror = abort

    await waitForTransaction(tx)
    return cleared
  }

  async updateSpaceFlagsForVolume(
    authority: LocalVolumeAuthority,
    spaceId: string,
    patch: LocalSpaceFlagPatch,
    now = new Date().toISOString()
  ): Promise<boolean> {
    const tx = this.requireDb().transaction(
      [LOCAL_STATE_STORE, SPACES_META_STORE],
      'readwrite'
    )
    const localStateStore = tx.objectStore(LOCAL_STATE_STORE)
    const metaStore = tx.objectStore(SPACES_META_STORE)
    let passport: LocalAccountPassport | undefined
    let meta: LocalSpaceMeta | undefined
    let pendingReads = 2
    let updated = false

    const evaluate = (): void => {
      if (!matchesPassportAuthority(passport, authority)) return
      if (!meta || meta.owner_id !== (authority?.owner_id ?? null)) return
      metaStore.put(applyLocalSpaceFlagPatch(meta, patch, now))
      updated = true
    }
    const finishRead = (): void => {
      pendingReads -= 1
      if (pendingReads === 0) evaluate()
    }
    const abort = (): void => tx.abort()

    const passportRequest = localStateStore.get(LOCAL_STATE_PASSPORT_KEY)
    passportRequest.onsuccess = () => {
      passport = (passportRequest.result as LocalPassportRow | undefined)?.value
      finishRead()
    }
    passportRequest.onerror = abort

    const metaRequest = metaStore.get(spaceId)
    metaRequest.onsuccess = () => {
      meta = metaRequest.result as LocalSpaceMeta | undefined
      finishRead()
    }
    metaRequest.onerror = abort

    await waitForTransaction(tx)
    return updated
  }

  async listSpacesMeta(): Promise<LocalSpaceMeta[]> {
    const rows = await readAll<LocalSpaceMeta>(this.requireDb(), SPACES_META_STORE)
    return rows.sort(compareLocalSpaceMeta)
  }

  async listVolumeSpacesMeta(
    authority: LocalVolumeAuthority
  ): Promise<LocalSpaceMeta[] | undefined> {
    const ownerId = authority?.owner_id ?? null
    const result = await this.runVolumeTransaction<LocalSpaceMeta[]>(
      authority,
      [SPACES_META_STORE],
      'readonly',
      (tx, settle) => {
        const request = tx.objectStore(SPACES_META_STORE).getAll()
        request.onsuccess = () => {
          const rows = [...(request.result ?? [])] as LocalSpaceMeta[]
          if (rows.some(row => row.owner_id !== ownerId)) return
          settle(rows.map(row => cloneLocalSpaceMeta(row)).sort(compareLocalSpaceMeta))
        }
        request.onerror = () => tx.abort()
      }
    )
    return result.authorized ? result.value : undefined
  }

  async listVolumeMemoryRows(
    authority: LocalVolumeAuthority
  ): Promise<LocalMemoryIndexRow[] | undefined> {
    const ownerId = authority?.owner_id ?? null
    const result = await this.runVolumeTransaction<LocalMemoryIndexRow[]>(
      authority,
      [SPACES_META_STORE, SPACES_BLOBS_STORE],
      'readonly',
      (tx, settle) => {
        const metaRequest = tx.objectStore(SPACES_META_STORE).getAll()
        const blobKeysRequest = tx.objectStore(SPACES_BLOBS_STORE).getAllKeys()
        let rows: LocalSpaceMeta[] = []
        let blobKeys: IDBValidKey[] = []
        let pending = 2
        const finish = (): void => {
          pending -= 1
          if (pending !== 0) return
          if (rows.some(row => row.owner_id !== ownerId)) return
          const blobSpaceIds = new Set(
            blobKeys.filter((key): key is string => typeof key === 'string')
          )
          settle(rows.sort(compareLocalSpaceMeta).map(row => ({
            owner_id: row.owner_id,
            space_id: row.space_id,
            space_slug: row.space_slug,
            space_ed: row.space_ed,
            size_bytes: row.size_bytes,
            deleted_at: row.deleted_at,
            last_opened_at: row.last_opened_at,
            availability: deriveLocalSpaceAvailability(
              row,
              blobSpaceIds.has(row.space_id)
            )
          })))
        }
        metaRequest.onsuccess = () => {
          rows = [...(metaRequest.result ?? [])] as LocalSpaceMeta[]
          finish()
        }
        metaRequest.onerror = () => tx.abort()
        blobKeysRequest.onsuccess = () => {
          blobKeys = [...(blobKeysRequest.result ?? [])]
          finish()
        }
        blobKeysRequest.onerror = () => tx.abort()
      }
    )
    return result.authorized ? result.value : undefined
  }

  async loadVolumeShelfSpace(
    authority: LocalVolumeAuthority,
    spaceId: string
  ): Promise<LocalShelfSpace | undefined> {
    const ownerId = authority?.owner_id ?? null
    const result = await this.runVolumeTransaction<LocalShelfSpace>(
      authority,
      [SPACES_META_STORE, SPACES_BLOBS_STORE],
      'readonly',
      (tx, settle) => {
        const metaRequest = tx.objectStore(SPACES_META_STORE).get(spaceId)
        const blobRequest = tx.objectStore(SPACES_BLOBS_STORE).get(spaceId)
        let meta: LocalSpaceMeta | undefined
        let blob: SpaceBlobRow | undefined
        let pending = 2
        const finish = (): void => {
          pending -= 1
          if (pending !== 0 || !meta || meta.owner_id !== ownerId) return
          settle({
            meta: cloneLocalSpaceMeta(meta),
            blob: blob ? { ...blob } : undefined
          })
        }
        metaRequest.onsuccess = () => {
          meta = metaRequest.result as LocalSpaceMeta | undefined
          finish()
        }
        metaRequest.onerror = () => tx.abort()
        blobRequest.onsuccess = () => {
          blob = blobRequest.result as SpaceBlobRow | undefined
          finish()
        }
        blobRequest.onerror = () => tx.abort()
      }
    )
    return result.authorized ? result.value : undefined
  }

  async preflightVolumeShelfExport(
    authority: LocalVolumeAuthority
  ): Promise<VolumeShelfExportPreflight> {
    const ownerId = authority?.owner_id ?? null
    let evaluated: VolumeShelfExportPreflight | undefined
    const result = await this.runVolumeTransaction<VolumeShelfExportPreflight>(
      authority,
      [SPACES_META_STORE, SPACES_BLOBS_STORE],
      'readonly',
      (tx, settle) => {
        const metaRequest = tx.objectStore(SPACES_META_STORE).getAll()
        const blobRequest = tx.objectStore(SPACES_BLOBS_STORE).getAll()
        let rows: LocalSpaceMeta[] = []
        let blobs: SpaceBlobRow[] = []
        let pending = 2
        const finish = (): void => {
          pending -= 1
          if (pending !== 0) return
          const metaById = new Map(rows.map(row => [row.space_id, row]))
          if (
            rows.some(row => row.owner_id !== ownerId)
            || blobs.some(blob => !metaById.has(blob.space_id))
          ) {
            evaluated = { ok: false, reason: 'volume_mismatch' }
            settle(evaluated)
            return
          }
          const blobById = new Map(blobs.map(blob => [blob.space_id, blob]))
          const liveRows = rows
            .filter(row => !row.deleted_at)
            .sort(compareLocalSpaceMeta)
          const incomplete = liveRows.find(row => (
            row.payload_state !== 'hydrated'
            || !blobById.has(row.space_id)
          ))
          if (incomplete) {
            evaluated = {
              ok: false,
              reason: 'incomplete',
              space_id: incomplete.space_id
            }
            settle(evaluated)
            return
          }
          evaluated = {
            ok: true,
            spaces: liveRows.map(meta => ({
              meta: cloneLocalSpaceMeta(meta),
              blob: { ...blobById.get(meta.space_id)! }
            }))
          }
          settle(evaluated)
        }
        metaRequest.onsuccess = () => {
          rows = [...(metaRequest.result ?? [])] as LocalSpaceMeta[]
          finish()
        }
        metaRequest.onerror = () => tx.abort()
        blobRequest.onsuccess = () => {
          blobs = [...(blobRequest.result ?? [])] as SpaceBlobRow[]
          finish()
        }
        blobRequest.onerror = () => tx.abort()
      }
    )
    if (!result.authorized) {
      return { ok: false, reason: 'authority_mismatch' }
    }
    return result.value ?? evaluated ?? { ok: false, reason: 'volume_mismatch' }
  }

  async isVolumeAuthorityCurrent(authority: LocalVolumeAuthority): Promise<boolean> {
    const result = await this.runVolumeTransaction<boolean>(
      authority,
      [],
      'readonly',
      (_tx, settle) => settle(true)
    )
    return result.authorized && result.value === true
  }

  async loadSpaceMeta(spaceId: string): Promise<LocalSpaceMeta | undefined> {
    return readOne<LocalSpaceMeta>(this.requireDb(), SPACES_META_STORE, spaceId)
  }

  async loadSpaceBlob(spaceId: string): Promise<SpaceBlobRow | undefined> {
    return readOne<SpaceBlobRow>(this.requireDb(), SPACES_BLOBS_STORE, spaceId)
  }

  async writeLocalSpace(input: WriteLocalSpaceInput): Promise<LocalSpaceMeta> {
    const db = this.requireDb()
    const now = input.now ?? new Date().toISOString()
    const payload = await encodeSpacePayload(input.blocksWithMeta)
    const nextMeta = buildLocalSpaceMeta(input.blocksWithMeta, payload, {
      ownerId: input.ownerId,
      spaceSlug: input.spaceSlug,
      dirty: input.dirty,
      payloadState: input.payloadState,
      now
    })
    const existing = await this.loadSpaceMeta(nextMeta.space_id)
    const meta = mergeLocalSpaceWriteMeta(existing, nextMeta, input)

    const tx = db.transaction([SPACES_META_STORE, SPACES_BLOBS_STORE], 'readwrite')
    tx.objectStore(SPACES_BLOBS_STORE).put({ space_id: meta.space_id, payload })
    tx.objectStore(SPACES_META_STORE).put(meta)
    await waitForTransaction(tx)
    return { ...meta }
  }

  async upsertRemoteSpacesMetaForVolume(
    authority: LocalVolumeAuthority,
    input: RemoteSpaceMetaInput[],
    now = new Date().toISOString()
  ): Promise<LocalSpaceMeta[] | undefined> {
    const ownerId = authority?.owner_id ?? null
    if (input.some(remote => remote.owner_id !== ownerId)) return undefined
    const result = await this.runVolumeTransaction<LocalSpaceMeta[]>(
      authority,
      [SPACES_META_STORE],
      'readwrite',
      (tx, settle) => {
        const store = tx.objectStore(SPACES_META_STORE)
        const request = store.getAll()
        request.onsuccess = () => {
          const rows = [...(request.result ?? [])] as LocalSpaceMeta[]
          if (rows.some(row => row.owner_id !== ownerId)) return
          const byId = new Map(rows.map(row => [row.space_id, row]))
          const written = input.map(remote => (
            buildRemoteKnownMeta(byId.get(remote.space_id), remote, now)
          ))
          written.forEach(meta => store.put(meta))
          settle(written.map(meta => cloneLocalSpaceMeta(meta)))
        }
        request.onerror = () => tx.abort()
      }
    )
    return result.authorized ? result.value : undefined
  }

  async replaceVolumeBlobFromRemote(
    authority: LocalVolumeAuthority,
    input: ReplaceLocalBlobFromRemoteInput
  ): Promise<ReplaceLocalBlobFromRemoteResult> {
    const ownerId = authority?.owner_id ?? null
    if (input.remoteMeta.owner_id !== ownerId) {
      return { ok: false, reason: 'owner_mismatch' }
    }
    let blocksWithMeta: SpaceEntry[]
    try {
      blocksWithMeta = await decodeSpacePayload(input.payload)
    } catch {
      return { ok: false, reason: 'invalid_payload' }
    }
    const payloadIdentity = getPayloadIdentity(blocksWithMeta)
    if (payloadIdentity.spaceId && payloadIdentity.spaceId !== input.remoteMeta.space_id) {
      return { ok: false, reason: 'invalid_payload' }
    }
    const remoteSpaceEd = input.remoteMeta.space_ed?.trim()
    if (remoteSpaceEd && payloadIdentity.spaceEd && payloadIdentity.spaceEd !== remoteSpaceEd) {
      return { ok: false, reason: 'invalid_payload' }
    }

    const payload = await encodeSpacePayload(blocksWithMeta)
    const now = input.now ?? new Date().toISOString()
    let outcome: ReplaceLocalBlobFromRemoteResult = {
      ok: false,
      reason: 'owner_mismatch'
    }
    const result = await this.runVolumeTransaction<ReplaceLocalBlobFromRemoteResult>(
      authority,
      [SPACES_META_STORE, SPACES_BLOBS_STORE],
      'readwrite',
      (tx, settle) => {
        const metaStore = tx.objectStore(SPACES_META_STORE)
        const request = metaStore.get(input.remoteMeta.space_id)
        request.onsuccess = () => {
          const existing = request.result as LocalSpaceMeta | undefined
          if (existing && existing.owner_id !== ownerId) {
            outcome = { ok: false, reason: 'owner_mismatch' }
            settle(outcome)
            return
          }
          if (existing?.dirty && !input.intent) {
            outcome = { ok: false, reason: 'local_dirty' }
            settle(outcome)
            return
          }
          if (existing?.deleted_at && !input.intent) {
            outcome = { ok: false, reason: 'local_deleted' }
            settle(outcome)
            return
          }
          const meta = buildRemoteHydratedMeta(
            existing,
            input.remoteMeta,
            payload,
            blocksWithMeta,
            now
          )
          tx.objectStore(SPACES_BLOBS_STORE).put({
            space_id: meta.space_id,
            payload
          })
          metaStore.put(meta)
          outcome = { ok: true, meta: cloneLocalSpaceMeta(meta) }
          settle(outcome)
        }
        request.onerror = () => tx.abort()
      }
    )
    if (!result.authorized) return { ok: false, reason: 'owner_mismatch' }
    return result.value ?? outcome
  }

  async recordVolumeSyncSuccess(
    authority: LocalVolumeAuthority,
    spaceId: string,
    syncAt: string | null,
    now = new Date().toISOString(),
    canonical?: SyncAcknowledgement
  ): Promise<boolean> {
    const ownerId = authority?.owner_id ?? null
    const sentEntries = canonical?.sent?.payload ? await decodeSpacePayload(canonical.sent.payload) : undefined
    const sentById = sentEntries && new Map(sentEntries.map(entry => [entry.ided[0], entry]))
    let updated = false
    const result = await this.runVolumeTransaction<boolean>(
      authority,
      [SPACES_META_STORE, SPACES_BLOBS_STORE, EDITOR_BLOCKS_STORE, LOCAL_STATE_STORE],
      'readwrite',
      (tx, settle) => {
        const store = tx.objectStore(SPACES_META_STORE)
        const request = store.get(spaceId)
        const blobRequest = tx.objectStore(SPACES_BLOBS_STORE).get(spaceId)
        let payload: string | undefined
        const editorRequest = tx.objectStore(EDITOR_BLOCKS_STORE).getAll()
        const markerRequest = tx.objectStore(LOCAL_STATE_STORE).get(LOCAL_STATE_WORKBENCH_KEY)
        let reads = 4
        const finish = () => {
          if (--reads !== 0) return
          const row = request.result as LocalSpaceMeta | undefined
          if (!row || row.owner_id !== ownerId) {
            settle(false)
            return
          }
          const marker = (markerRequest.result as LocalWorkbenchRow | undefined)?.value
          const hasUnsentWorkbench = sentById && marker?.space_id === spaceId
            && matchesVolumeMarkerAuthority(marker, authority)
            && (editorRequest.result as SpaceEntry[]).some(entry => {
              const sent = sentById.get(entry.ided[0])
              return !sent || entry.ided[1] > sent.ided[1]
            })
          const matches = !hasUnsentWorkbench && (!canonical?.sent || (
            row.space_ed === canonical.sent.spaceEd
            && (canonical.sent.payload === undefined || payload === canonical.sent.payload)
          ))
          if (!matches) {
            store.put({ ...row, dirty: true, dirty_since: row.dirty_since ?? now, sync_at: syncAt ?? row.sync_at, last_sync_ok_at: now })
            updated = true
            settle(true)
            return
          }
          store.put({
            ...row,
            space_slug: canonical?.spaceSlug !== undefined ? canonical.spaceSlug : row.space_slug,
            space_ed: canonical?.spaceEd ?? row.space_ed,
            dirty: false,
            dirty_since: null,
            sync_at: syncAt ?? row.sync_at,
            payload_state: row.payload_state === 'missing' ? 'hydrated' : row.payload_state,
            payload_error_at: null,
            last_sync_ok_at: now,
            in_flight_tab_id: null,
            in_flight_started_at: null
          })
          updated = true
          settle(true)
        }
        request.onsuccess = finish
        request.onerror = () => tx.abort()
        blobRequest.onsuccess = () => {
          payload = (blobRequest.result as SpaceBlobRow | undefined)?.payload
          finish()
        }
        blobRequest.onerror = () => tx.abort()
        editorRequest.onsuccess = finish
        editorRequest.onerror = () => tx.abort()
        markerRequest.onsuccess = finish
        markerRequest.onerror = () => tx.abort()
      }
    )
    return result.authorized && (result.value ?? updated)
  }

  async markVolumeSpaceDeleted(
    authority: LocalVolumeAuthority,
    spaceId: string,
    deletedAt: string,
    now = new Date().toISOString()
  ): Promise<LocalSpaceMeta | undefined> {
    const ownerId = authority?.owner_id ?? null
    const result = await this.runVolumeTransaction<LocalSpaceMeta>(
      authority,
      [EDITOR_BLOCKS_STORE, SPACES_META_STORE],
      'readwrite',
      (tx, settle) => {
        const localStateStore = tx.objectStore(LOCAL_STATE_STORE)
        const editorStore = tx.objectStore(EDITOR_BLOCKS_STORE)
        const store = tx.objectStore(SPACES_META_STORE)
        let row: LocalSpaceMeta | undefined
        let marker: LocalWorkbenchMarker | undefined
        let editorBlocks: SpaceEntry[] = []
        let pendingReads = 3
        const finishRead = (): void => {
          pendingReads -= 1
          if (pendingReads !== 0) return
          if (
            !row
            || row.owner_id !== ownerId
            || (marker && !matchesVolumeMarkerAuthority(marker, authority))
          ) return
          const isActive = marker?.space_id === spaceId
          if (isActive) {
            const { metaBlock } = extractLilodataBlock(editorBlocks)
            if (editorBlocks.length && metaBlock?.ided[0] !== spaceId) return
          }
          const next: LocalSpaceMeta = {
            ...row,
            space_ed: deletedAt,
            deleted_at: deletedAt,
            dirty: true,
            dirty_since: row.dirty ? row.dirty_since : now,
            payload_error_at: null,
            last_local_save_at: now
          }
          store.put(next)
          if (isActive) {
            editorStore.clear()
            localStateStore.delete(LOCAL_STATE_WORKBENCH_KEY)
          }
          settle(cloneLocalSpaceMeta(next))
        }
        const request = store.get(spaceId)
        request.onsuccess = () => {
          row = request.result as LocalSpaceMeta | undefined
          finishRead()
        }
        request.onerror = () => tx.abort()
        const markerRequest = localStateStore.get(LOCAL_STATE_WORKBENCH_KEY)
        markerRequest.onsuccess = () => {
          marker = (markerRequest.result as LocalWorkbenchRow | undefined)?.value
          finishRead()
        }
        markerRequest.onerror = () => tx.abort()
        const editorRequest = editorStore.getAll()
        editorRequest.onsuccess = () => {
          editorBlocks = ((editorRequest.result ?? []) as SpaceEntry[]).map(block => cloneSpaceEntry(block))
          finishRead()
        }
        editorRequest.onerror = () => tx.abort()
      }
    )
    return result.authorized ? result.value : undefined
  }

  async purgeDeletedVolumePayload(
    authority: LocalVolumeAuthority,
    spaceId: string,
    now = new Date().toISOString()
  ): Promise<LocalSpaceMeta | undefined> {
    const ownerId = authority?.owner_id ?? null
    const result = await this.runVolumeTransaction<LocalSpaceMeta>(
      authority,
      [SPACES_META_STORE, SPACES_BLOBS_STORE],
      'readwrite',
      (tx, settle) => {
        const metaStore = tx.objectStore(SPACES_META_STORE)
        const request = metaStore.get(spaceId)
        request.onsuccess = () => {
          const row = request.result as LocalSpaceMeta | undefined
          if (!row?.deleted_at || row.owner_id !== ownerId) return
          tx.objectStore(SPACES_BLOBS_STORE).delete(spaceId)
          const next: LocalSpaceMeta = {
            ...row,
            size_bytes: 0,
            payload_state: 'missing',
            payload_error_at: null,
            last_local_save_at: now
          }
          metaStore.put(next)
          settle(cloneLocalSpaceMeta(next))
        }
        request.onerror = () => tx.abort()
      }
    )
    return result.authorized ? result.value : undefined
  }

  async listUnsafeVolumeRows(
    authority: MountedVolumeAuthority
  ): Promise<UnsafeOwnerShelfRow[] | undefined> {
    const rows = await this.listVolumeSpacesMeta(authority)
    if (!rows) return undefined
    return rows.flatMap(row => {
      const reason = resolveUnsafeOwnerShelfReason(row)
      return reason
        ? [{ meta: cloneLocalSpaceMeta(row), reason }]
        : []
    })
  }

  async renameVolumeSpace(
    authority: LocalVolumeAuthority,
    spaceId: string,
    spaceSlug: string,
    spaceEditedId: string,
    now = new Date().toISOString(),
    expectedPayload?: string
  ): Promise<LocalSpaceMeta | undefined> {
    const ownerId = authority?.owner_id ?? null
    const source = await this.loadVolumeShelfSpace(authority, spaceId)
    if (!source || source.meta.deleted_at) return undefined
    let nextPayload: string | undefined
    let expectedEdById: Map<string, string> | undefined
    const sourcePayload = source.blob?.payload
    if (sourcePayload) {
      const blocksWithMeta = await decodeSpacePayload(sourcePayload)
      if (expectedPayload !== undefined) {
        expectedEdById = new Map(blocksWithMeta.map(entry => [entry.ided[0], entry.ided[1]]))
      }
      const { metaBlock } = extractLilodataBlock(blocksWithMeta)
      if (
        !metaBlock
        || metaBlock.ided[0] !== spaceId
        || metaBlock.ided[1] !== source.meta.space_ed
      ) return undefined
      nextPayload = await encodeSpacePayload(
        updateBlocksMetaSlug(blocksWithMeta, spaceId, spaceSlug, spaceEditedId)
      )
    }

    const result = await this.runVolumeTransaction<LocalSpaceMeta>(
      authority,
      [EDITOR_BLOCKS_STORE, SPACES_META_STORE, SPACES_BLOBS_STORE],
      'readwrite',
      (tx, settle) => {
        const localStateStore = tx.objectStore(LOCAL_STATE_STORE)
        const metaStore = tx.objectStore(SPACES_META_STORE)
        const metaRequest = metaStore.getAll()
        const blobRequest = tx.objectStore(SPACES_BLOBS_STORE).get(spaceId)
        const editorRequest = tx.objectStore(EDITOR_BLOCKS_STORE).getAll()
        const markerRequest = localStateStore.get(LOCAL_STATE_WORKBENCH_KEY)
        let rows: LocalSpaceMeta[] = []
        let blob: SpaceBlobRow | undefined
        let editorBlocks: SpaceEntry[] = []
        let marker: LocalWorkbenchMarker | undefined
        let pending = 4
        const finish = (): void => {
          pending -= 1
          if (pending !== 0) return
          const target = rows.find(row => row.space_id === spaceId)
          if (
            !target
            || target.deleted_at
            || target.owner_id !== ownerId
            || rows.some(row => row.owner_id !== ownerId)
            || rows.some(row => (
              !row.deleted_at
              && row.space_id !== spaceId
              && row.space_slug === spaceSlug
            ))
            || blob?.payload !== sourcePayload
            // Conflict fallback belongs to the uploaded revision, not a later rename.
            || (expectedPayload !== undefined && (
              blob?.payload !== expectedPayload || target.space_ed !== source.meta.space_ed
            ))
          ) return

          const { metaBlock: editorMeta } = extractLilodataBlock(editorBlocks)
          const shouldUpdateEditor = editorMeta?.ided[0] === spaceId
          // The workbench may contain writing not yet packed into the shelf blob.
          if (expectedEdById && shouldUpdateEditor && editorBlocks.some(entry => {
            const expectedEd = expectedEdById!.get(entry.ided[0])
            return expectedEd === undefined || entry.ided[1] > expectedEd
          })) return
          if (
            (marker && !matchesVolumeMarkerAuthority(marker, authority))
            || (marker?.space_id === spaceId && !shouldUpdateEditor)
            || (
              shouldUpdateEditor
              && (!marker || marker.space_id !== spaceId)
            )
          ) return

          const nextMeta: LocalSpaceMeta = {
            ...target,
            space_slug: spaceSlug,
            space_ed: spaceEditedId,
            size_bytes: nextPayload ? measureUtf8Bytes(nextPayload) : target.size_bytes,
            dirty: true,
            dirty_since: target.dirty ? target.dirty_since : now,
            last_local_save_at: now,
            payload_state: nextPayload ? 'hydrated' : target.payload_state,
            payload_error_at: nextPayload ? null : target.payload_error_at
          }
          metaStore.put(nextMeta)
          if (nextPayload) {
            tx.objectStore(SPACES_BLOBS_STORE).put({
              space_id: spaceId,
              payload: nextPayload
            })
          }
          if (shouldUpdateEditor) {
            const editorStore = tx.objectStore(EDITOR_BLOCKS_STORE)
            editorStore.clear()
            updateBlocksMetaSlug(
              editorBlocks,
              spaceId,
              spaceSlug,
              spaceEditedId
            ).forEach(block => editorStore.put(cloneSpaceEntry(block), block.ided[0]))
          }
          settle(cloneLocalSpaceMeta(nextMeta))
        }
        metaRequest.onsuccess = () => {
          rows = [...(metaRequest.result ?? [])] as LocalSpaceMeta[]
          finish()
        }
        metaRequest.onerror = () => tx.abort()
        blobRequest.onsuccess = () => {
          blob = blobRequest.result as SpaceBlobRow | undefined
          finish()
        }
        blobRequest.onerror = () => tx.abort()
        editorRequest.onsuccess = () => {
          editorBlocks = ((editorRequest.result ?? []) as SpaceEntry[]).map(block => cloneSpaceEntry(block))
          finish()
        }
        editorRequest.onerror = () => tx.abort()
        markerRequest.onsuccess = () => {
          marker = (markerRequest.result as LocalWorkbenchRow | undefined)?.value
          finish()
        }
        markerRequest.onerror = () => tx.abort()
      }
    )
    return result.authorized ? result.value : undefined
  }

  async deleteVolumeSpace(
    authority: LocalVolumeAuthority,
    spaceId: string,
    seedProof?: SeedSpaceProof
  ): Promise<boolean> {
    const ownerId = authority?.owner_id ?? null
    const result = await this.runVolumeTransaction<boolean>(
      authority,
      [EDITOR_BLOCKS_STORE, SPACES_META_STORE, SPACES_BLOBS_STORE],
      'readwrite',
      (tx, settle) => {
        const localStateStore = tx.objectStore(LOCAL_STATE_STORE)
        const editorStore = tx.objectStore(EDITOR_BLOCKS_STORE)
        const metaStore = tx.objectStore(SPACES_META_STORE)
        const metaRequest = metaStore.get(spaceId)
        const markerRequest = localStateStore.get(LOCAL_STATE_WORKBENCH_KEY)
        const blobRequest = tx.objectStore(SPACES_BLOBS_STORE).get(spaceId)
        let meta: LocalSpaceMeta | undefined
        let marker: LocalWorkbenchMarker | undefined
        let blob: SpaceBlobRow | undefined
        let editorBlocks: SpaceEntry[] = []
        let pending = 4
        const finish = (): void => {
          pending -= 1
          if (pending !== 0) return
          if (
            !meta
            || meta.owner_id !== ownerId
            || (marker && !matchesVolumeMarkerAuthority(marker, authority))
            || (seedProof && !matchesCleanSeedProof(meta, blob, seedProof))
          ) {
            settle(false)
            return
          }
          const isActive = marker?.space_id === spaceId
          if (isActive) {
            const { metaBlock } = extractLilodataBlock(editorBlocks)
            if (editorBlocks.length && metaBlock?.ided[0] !== spaceId) {
              settle(false)
              return
            }
          }
          metaStore.delete(spaceId)
          tx.objectStore(SPACES_BLOBS_STORE).delete(spaceId)
          if (isActive) {
            editorStore.clear()
            localStateStore.delete(LOCAL_STATE_WORKBENCH_KEY)
          }
          settle(true)
        }
        metaRequest.onsuccess = () => {
          meta = metaRequest.result as LocalSpaceMeta | undefined
          finish()
        }
        metaRequest.onerror = () => tx.abort()
        markerRequest.onsuccess = () => {
          marker = (markerRequest.result as LocalWorkbenchRow | undefined)?.value
          finish()
        }
        markerRequest.onerror = () => tx.abort()
        blobRequest.onsuccess = () => {
          blob = blobRequest.result as SpaceBlobRow | undefined
          finish()
        }
        blobRequest.onerror = () => tx.abort()
        const editorRequest = editorStore.getAll()
        editorRequest.onsuccess = () => {
          editorBlocks = ((editorRequest.result ?? []) as SpaceEntry[]).map(block => cloneSpaceEntry(block))
          finish()
        }
        editorRequest.onerror = () => tx.abort()
      }
    )
    return result.authorized && result.value === true
  }

  private async runVolumeTransaction<T>(
    authority: LocalVolumeAuthority,
    storeNames: string[],
    mode: IDBTransactionMode,
    operation: (tx: IDBTransaction, settle: (value: T) => void) => void
  ): Promise<{ authorized: boolean, value?: T }> {
    const names = [...new Set([LOCAL_STATE_STORE, ...storeNames])]
    const tx = this.requireDb().transaction(names, mode)
    const request = tx.objectStore(LOCAL_STATE_STORE).get(LOCAL_STATE_PASSPORT_KEY)
    let authorized = false
    let value: T | undefined
    let operationError: unknown
    request.onsuccess = () => {
      const passport = (request.result as LocalPassportRow | undefined)?.value
      if (!matchesPassportAuthority(passport, authority)) return
      authorized = true
      try {
        operation(tx, next => {
          value = next
        })
      } catch (error) {
        operationError = error
        tx.abort()
      }
    }
    request.onerror = () => tx.abort()
    try {
      await waitForTransaction(tx)
    } catch (error) {
      if (operationError) throw operationError
      throw error
    }
    return { authorized, value }
  }

  private requireDb(): IDBDatabase {
    if (!this.db) throw new Error('Database memory is not open')
    return this.db
  }
}

function ensureObjectStores(db: IDBDatabase): void {
  if (!db.objectStoreNames.contains(EDITOR_BLOCKS_STORE)) {
    db.createObjectStore(EDITOR_BLOCKS_STORE)
  }
  if (!db.objectStoreNames.contains(SPACES_META_STORE)) {
    db.createObjectStore(SPACES_META_STORE, { keyPath: 'space_id' })
  }
  if (!db.objectStoreNames.contains(SPACES_BLOBS_STORE)) {
    db.createObjectStore(SPACES_BLOBS_STORE, { keyPath: 'space_id' })
  }
  if (!db.objectStoreNames.contains(LOCAL_STATE_STORE)) {
    db.createObjectStore(LOCAL_STATE_STORE, { keyPath: 'key' })
  }
}

function migrateLegacyBlocksDuringUpgrade(db: IDBDatabase, tx: IDBTransaction): void {
  if (!db.objectStoreNames.contains(LEGACY_BLOCKS_STORE)) return

  const legacyStore = tx.objectStore(LEGACY_BLOCKS_STORE)
  const editorStore = tx.objectStore(EDITOR_BLOCKS_STORE)
  const metaStore = tx.objectStore(SPACES_META_STORE)
  const blobStore = tx.objectStore(SPACES_BLOBS_STORE)
  const request = legacyStore.getAll()

  request.onsuccess = () => {
    const legacyBlocks = sortBlocks(((request.result ?? []) as SpaceEntry[]).map(block => cloneSpaceEntry(block)))
    if (!legacyBlocks.length) return

    const blocksWithMeta = ensureSpaceMetaBlock(legacyBlocks)
    for (const block of blocksWithMeta) {
      editorStore.put(cloneSpaceEntry(block), block.ided[0])
    }

    const payload = JSON.stringify(blocksWithMeta.map(block => cloneSpaceEntry(block)))
    const localMeta = buildLocalSpaceMeta(blocksWithMeta, payload, {
      dirty: false,
      payloadState: 'hydrated',
      now: new Date().toISOString()
    })
    metaStore.put(localMeta)
    blobStore.put({ space_id: localMeta.space_id, payload })
  }
  request.onerror = () => tx.abort()
  db.deleteObjectStore(LEGACY_BLOCKS_STORE)
}

function ensureSpaceMetaBlock(blocksWithPossibleMeta: SpaceEntry[]): SpaceEntry[] {
  const cloned = blocksWithPossibleMeta.map(block => cloneSpaceEntry(block))
  const { blocks, metaBlock } = extractLilodataBlock(cloned)
  const normalizedMeta = normalizeMetaBlock(blocks, metaBlock)
  return normalizedMeta ? [...blocks, normalizedMeta] : blocks
}

function normalizeMetaBlock(blocks: SpaceEntry[], metaBlock?: Block): Block | undefined {
  if (!metaBlock) {
    const derived = deriveSpaceIded(blocks)
    return createLilodataBlock('', {
      createdId: derived.createdId,
      editedId: derived.editedId
    })
  }
  const isLegacy = metaBlock.ided[0] === LILODATA_BLOCK_ID || metaBlock.ided[1] === LILODATA_BLOCK_ID
  if (!isLegacy) return cloneBlock(metaBlock)
  const derived = deriveSpaceIded(blocks)
  return {
    ...cloneBlock(metaBlock),
    ided: [derived.createdId, derived.editedId]
  }
}

function buildLocalSpaceMeta(
  blocksWithMeta: SpaceEntry[],
  payload: string,
  opts: {
    ownerId?: string | null
    spaceSlug?: string | null
    dirty?: boolean
    payloadState?: PayloadState
    now: string
  }
): LocalSpaceMeta {
  const { blocks, metaBlock } = extractLilodataBlock(blocksWithMeta.map(block => cloneSpaceEntry(block)))
  const identity = metaBlock
    ? { createdId: metaBlock.ided[0], editedId: metaBlock.ided[1] }
    : deriveSpaceIded(blocks)
  const parsedSlug = metaBlock ? parseLilodataText(metaBlock.text).slug?.trim() : undefined
  const dirty = opts.dirty ?? false

  return {
    owner_id: opts.ownerId ?? null,
    space_id: identity.createdId,
    space_slug: opts.spaceSlug ?? (parsedSlug || null),
    space_ed: identity.editedId,
    size_bytes: measureUtf8Bytes(payload),
    published: false,
    sync_at: null,
    deleted_at: null,
    dirty,
    dirty_since: dirty ? opts.now : null,
    payload_state: opts.payloadState ?? 'hydrated',
    payload_error_at: null,
    last_opened_at: opts.now,
    last_local_save_at: opts.now,
    last_sync_ok_at: null,
    in_flight_tab_id: null,
    in_flight_started_at: null
  }
}

function mergeLocalSpaceWriteMeta(
  existing: LocalSpaceMeta | undefined,
  next: LocalSpaceMeta,
  input: WriteLocalSpaceInput
): LocalSpaceMeta {
  if (!existing) return next
  return {
    ...next,
    owner_id: input.ownerId === undefined ? existing.owner_id : next.owner_id,
    published: existing.published,
    sync_at: existing.sync_at,
    deleted_at: existing.deleted_at,
    dirty: input.dirty === undefined ? existing.dirty : next.dirty,
    dirty_since: input.dirty === undefined ? existing.dirty_since : next.dirty_since,
    payload_state: input.payloadState === undefined ? existing.payload_state : next.payload_state,
    payload_error_at: input.payloadState === undefined ? existing.payload_error_at : next.payload_error_at,
    last_sync_ok_at: existing.last_sync_ok_at,
    in_flight_tab_id: existing.in_flight_tab_id,
    in_flight_started_at: existing.in_flight_started_at
  }
}

function buildRemoteKnownMeta(
  existing: LocalSpaceMeta | undefined,
  remote: RemoteSpaceMetaInput,
  now: string
): LocalSpaceMeta {
  const terminalPayloadFailure = existing ? isTerminalPayloadFailure(existing) : false
  const terminalRevisionAdvanced = terminalPayloadFailure
    && isRemoteRevisionNewer(remote.space_ed, existing?.space_ed)
  const preserveLocalPayloadFacts =
    existing?.dirty === true
    || Boolean(existing?.deleted_at)
    || existing?.payload_state === 'hydrated'
    || (terminalPayloadFailure && !terminalRevisionAdvanced)
  return {
    owner_id: preserveLocalPayloadFacts ? existing?.owner_id ?? null : remote.owner_id,
    space_id: remote.space_id,
    space_slug: preserveLocalPayloadFacts ? existing?.space_slug ?? null : remote.space_slug ?? existing?.space_slug ?? null,
    space_ed: preserveLocalPayloadFacts ? existing?.space_ed ?? '' : remote.space_ed ?? existing?.space_ed ?? '',
    size_bytes: preserveLocalPayloadFacts ? existing?.size_bytes ?? 0 : remote.size_bytes ?? existing?.size_bytes ?? 0,
    published: remote.published ?? existing?.published ?? false,
    sync_at: preserveLocalPayloadFacts ? existing?.sync_at ?? null : remote.sync_at ?? existing?.sync_at ?? null,
    deleted_at: preserveLocalPayloadFacts ? existing?.deleted_at ?? null : remote.deleted_at ?? existing?.deleted_at ?? null,
    dirty: existing?.dirty ?? false,
    dirty_since: existing?.dirty_since ?? null,
    payload_state: terminalRevisionAdvanced ? 'known' : existing?.payload_state ?? 'known',
    payload_error_at: terminalRevisionAdvanced ? null : existing?.payload_error_at ?? null,
    last_opened_at: existing?.last_opened_at ?? null,
    last_local_save_at: existing?.last_local_save_at ?? null,
    last_sync_ok_at: preserveLocalPayloadFacts ? existing?.last_sync_ok_at ?? null : existing?.last_sync_ok_at ?? (remote.sync_at ? now : null),
    in_flight_tab_id: existing?.in_flight_tab_id ?? null,
    in_flight_started_at: existing?.in_flight_started_at ?? null
  }
}

function buildRemoteHydratedMeta(
  existing: LocalSpaceMeta | undefined,
  remote: RemoteSpaceMetaInput,
  payload: string,
  blocksWithMeta: SpaceEntry[],
  now: string
): LocalSpaceMeta {
  const { blocks, metaBlock } = extractLilodataBlock(blocksWithMeta.map(block => cloneSpaceEntry(block)))
  const identity = metaBlock
    ? { createdId: metaBlock.ided[0], editedId: metaBlock.ided[1] }
    : deriveSpaceIded(blocks)
  const parsedSlug = metaBlock ? parseLilodataText(metaBlock.text).slug?.trim() : undefined

  return {
    owner_id: remote.owner_id,
    space_id: remote.space_id || identity.createdId,
    space_slug: remote.space_slug ?? (parsedSlug || existing?.space_slug || null),
    space_ed: remote.space_ed ?? identity.editedId,
    size_bytes: measureUtf8Bytes(payload),
    published: remote.published ?? existing?.published ?? false,
    sync_at: remote.sync_at ?? existing?.sync_at ?? null,
    deleted_at: remote.deleted_at ?? null,
    dirty: false,
    dirty_since: null,
    payload_state: 'hydrated',
    payload_error_at: null,
    last_opened_at: existing?.last_opened_at ?? null,
    last_local_save_at: existing?.last_local_save_at ?? null,
    last_sync_ok_at: now,
    in_flight_tab_id: null,
    in_flight_started_at: null
  }
}

function getPayloadIdentity(blocksWithMeta: SpaceEntry[]): { spaceId?: string, spaceEd?: string } {
  const { blocks, metaBlock } = extractLilodataBlock(blocksWithMeta.map(block => cloneSpaceEntry(block)))
  if (metaBlock) return { spaceId: metaBlock.ided[0], spaceEd: metaBlock.ided[1] }
  if (!blocks.length) return {}
  const derived = deriveSpaceIded(blocks)
  return { spaceId: derived.createdId, spaceEd: derived.editedId }
}

function updateBlocksMetaSlug(blocksWithMeta: SpaceEntry[], spaceId: string, spaceSlug: string, spaceEditedId: string): SpaceEntry[] {
  return blocksWithMeta.map(block => {
    if (!isLilodataBlock(block) || block.ided[0] !== spaceId) return cloneSpaceEntry(block)
    return {
      ...cloneBlock(block),
      text: updateLilodataText(block.text, { slug: spaceSlug }),
      ided: [block.ided[0], spaceEditedId]
    }
  })
}

function applyLocalSpaceFlagPatch(
  row: LocalSpaceMeta,
  patch: LocalSpaceFlagPatch,
  now: string
): LocalSpaceMeta {
  let dirty = row.dirty
  let dirtySince = row.dirty_since
  if (patch.dirty !== undefined) {
    dirty = patch.dirty
    dirtySince = patch.dirty
      ? (row.dirty ? row.dirty_since : now)
      : null
  }

  return {
    ...row,
    space_ed: patch.spaceEd ?? row.space_ed,
    dirty,
    dirty_since: dirtySince,
    payload_state: patch.payloadState ?? row.payload_state,
    payload_error_at: patch.payloadErrorAt !== undefined ? patch.payloadErrorAt : row.payload_error_at,
    last_local_save_at: patch.lastLocalSaveAt ?? row.last_local_save_at
  }
}

export function isTerminalPayloadFailure(
  row: Pick<LocalSpaceMeta, 'payload_state' | 'payload_error_at'>
): boolean {
  return row.payload_state === 'missing'
    || (row.payload_state === 'known' && row.payload_error_at !== null)
}

export function deriveLocalSpaceAvailability(
  row: Pick<LocalSpaceMeta, 'payload_state' | 'payload_error_at'>,
  hasBlob: boolean
): LocalSpaceAvailability {
  if (isTerminalPayloadFailure(row)) return 'damaged'
  if (hasBlob) return 'ready'
  return row.payload_state === 'known' ? 'unavailable' : 'damaged'
}

function isRemoteRevisionNewer(
  remoteEd: string | null | undefined,
  localEd: string | null | undefined
): boolean {
  const normalizedRemote = typeof remoteEd === 'string' ? remoteEd.trim() : ''
  const normalizedLocal = typeof localEd === 'string' ? localEd.trim() : ''
  return Boolean(normalizedRemote && normalizedRemote.localeCompare(normalizedLocal) > 0)
}

function resolveUnsafeOwnerShelfReason(row: LocalSpaceMeta): UnsafeOwnerShelfReason | undefined {
  if (row.deleted_at && row.dirty) return 'tombstone_unsynced'
  if (row.dirty) return 'dirty'
  if (row.in_flight_tab_id || row.in_flight_started_at) return 'sync_in_flight'
  if (!row.last_sync_ok_at && !row.sync_at) return 'missing_sync_proof'
  return undefined
}

function matchesCleanSeedProof(
  meta: LocalSpaceMeta | undefined,
  blob: SpaceBlobRow | undefined,
  proof: SeedSpaceProof
): boolean {
  return Boolean(
    meta
    && blob
    && meta.space_id === proof.space_id
    && meta.space_ed === proof.space_ed
    && blob.space_id === proof.space_id
    && blob.payload === proof.payload
    && hasCleanSeedMetadata(meta)
  )
}

function matchesBlocksPayloadProof(editorBlocks: SpaceEntry[], proof: SpacePayloadProof): boolean {
  try {
    const parsed = JSON.parse(proof.payload)
    if (!Array.isArray(parsed)) return false
    const proofBlocks = parsed.map(item => cloneSpaceEntry(item as SpaceEntry))
    return matchesBlocksExactly(editorBlocks, proofBlocks)
  } catch {
    return false
  }
}

function matchesBlocksExactly(left: SpaceEntry[], right: SpaceEntry[]): boolean {
  return JSON.stringify(sortBlocks(left).map(block => cloneSpaceEntry(block)))
    === JSON.stringify(sortBlocks(right).map(block => cloneSpaceEntry(block)))
}

function matchesOptionalWorkbenchMarker(
  actual: LocalWorkbenchMarker | undefined,
  expected: LocalWorkbenchMarker | undefined
): boolean {
  if (!actual || !expected) return actual === expected
  return actual.owner_id === expected.owner_id
    && actual.mount_id === expected.mount_id
    && actual.space_id === expected.space_id
}

function matchesMountedAuthority(
  actual: MountedVolumeAuthority,
  expected: MountedVolumeAuthority
): boolean {
  return actual.owner_id === expected.owner_id && actual.mount_id === expected.mount_id
}

function matchesPassportAuthority(
  passport: LocalAccountPassport | undefined,
  expected: MountedVolumeAuthority | null
): boolean {
  if (!expected) return !passport
  return Boolean(passport && matchesMountedAuthority(passport, expected))
}

function matchesVolumeMarkerAuthority(
  marker: LocalWorkbenchMarker,
  authority: LocalVolumeAuthority
): boolean {
  return authority
    ? (
        marker.owner_id === authority.owner_id
        && marker.mount_id === authority.mount_id
      )
    : marker.owner_id === null && marker.mount_id === null
}

function matchesWorkbenchAuthority(
  passport: LocalAccountPassport | undefined,
  marker: LocalWorkbenchMarker
): boolean {
  if (marker.owner_id === null) return !passport
  return Boolean(passport && matchesMountedAuthority(passport, marker))
}

function readValidPassportRow(value: unknown): LocalAccountPassport | undefined {
  if (!value || typeof value !== 'object') return undefined
  const row = value as { key?: unknown, value?: unknown }
  if (row.key !== LOCAL_STATE_PASSPORT_KEY || !row.value || typeof row.value !== 'object') {
    return undefined
  }
  const passport = row.value as Record<string, unknown>
  if (
    (passport.kind !== 'unverified' && passport.kind !== 'verified')
    || !isExactNonEmptyString(passport.owner_id)
    || !isExactNonEmptyString(passport.mount_id)
  ) {
    return undefined
  }
  if (passport.kind === 'unverified') {
    return {
      kind: 'unverified',
      owner_id: passport.owner_id,
      mount_id: passport.mount_id
    }
  }
  if (!passport.profile || typeof passport.profile !== 'object') return undefined
  const profile = passport.profile as Record<string, unknown>
  if (
    (profile.username !== null && typeof profile.username !== 'string')
    || (profile.account_state !== 'sync' && profile.account_state !== 'deactivated')
    || typeof profile.stripe_cancel_at_period_end !== 'boolean'
    || (
      profile.stripe_current_period_end !== undefined
      && typeof profile.stripe_current_period_end !== 'string'
    )
  ) {
    return undefined
  }
  return {
    kind: 'verified',
    owner_id: passport.owner_id,
    mount_id: passport.mount_id,
    profile: {
      username: profile.username,
      account_state: profile.account_state,
      stripe_cancel_at_period_end: profile.stripe_cancel_at_period_end,
      ...(profile.stripe_current_period_end === undefined
        ? {}
        : { stripe_current_period_end: profile.stripe_current_period_end })
    }
  }
}

function readValidWorkbenchRow(value: unknown): LocalWorkbenchMarker | undefined {
  if (!value || typeof value !== 'object') return undefined
  const row = value as { key?: unknown, value?: unknown }
  if (row.key !== LOCAL_STATE_WORKBENCH_KEY || !row.value || typeof row.value !== 'object') {
    return undefined
  }
  const marker = row.value as Record<string, unknown>
  if (!isExactNonEmptyString(marker.space_id)) return undefined
  if (marker.owner_id === null && marker.mount_id === null) {
    return {
      owner_id: null,
      mount_id: null,
      space_id: marker.space_id
    }
  }
  if (!isExactNonEmptyString(marker.owner_id) || !isExactNonEmptyString(marker.mount_id)) {
    return undefined
  }
  return {
    owner_id: marker.owner_id,
    mount_id: marker.mount_id,
    space_id: marker.space_id
  }
}

function hasValidLocalSpaceIdentity(row: LocalSpaceMeta): boolean {
  return isExactNonEmptyString(row.space_id)
    && (row.owner_id === null || isExactNonEmptyString(row.owner_id))
}

function isExactNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && value.trim() === value
}

function cloneLocalAccountPassport(passport: LocalAccountPassport): LocalAccountPassport {
  if (passport.kind === 'unverified') {
    return { ...passport }
  }
  return {
    ...passport,
    profile: { ...passport.profile }
  }
}

function readAll<T>(db: IDBDatabase, storeName: string): Promise<T[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction([storeName], 'readonly')
    const request = tx.objectStore(storeName).getAll()
    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve([...(request.result ?? [])] as T[])
  })
}

function readOne<T>(db: IDBDatabase, storeName: string, key: IDBValidKey): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction([storeName], 'readonly')
    const request = tx.objectStore(storeName).get(key)
    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result as T | undefined)
  })
}

function measureUtf8Bytes(value: string): number {
  return new TextEncoder().encode(value).length
}

export function compareLocalSpaceMeta(a: LocalSpaceMeta, b: LocalSpaceMeta): number {
  const aOpened = a.last_opened_at ?? ''
  const bOpened = b.last_opened_at ?? ''
  if (aOpened !== bOpened) return bOpened.localeCompare(aOpened)
  return b.space_ed.localeCompare(a.space_ed)
}

function cloneLocalSpaceMeta(meta: LocalSpaceMeta): LocalSpaceMeta {
  return { ...meta }
}
